import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import UsageBanner from "../components/UsageBanner";
import LogoUploader from "../components/LogoUploader";
import { LeadScorePill, QualSummary } from "../components/SalesIntel";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Bot, Users, MessagesSquare, BarChart3, Upload, Trash2, FileText, FileSpreadsheet, ExternalLink, Copy, Loader2, RefreshCw, Mail, CheckCircle2, AlertCircle, XCircle, Send, Globe, ShieldAlert, ChevronDown, ChevronRight, Pencil, Plus, Eye, Download, Type, X, Sparkles, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { normalizeWebsiteUrl } from "../lib/url";
// Catalog Fulfillment Scheduling Phase 0 (Sep 2026) — reused rather than
// duplicated. Both helpers are pure IANA-zone utilities with no printing
// semantics; they happen to live in the printing model because the
// receipt timezone control needed them first.
import { timezoneProblem, timezoneSample } from "../lib/printing/printingModel";
import useUnsavedChangesGuard from "../lib/useUnsavedChangesGuard";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import DocumentEditorModal from "../components/DocumentEditorModal";
import ShowcaseDashboard from "../components/ShowcaseDashboard";
import SetupChecklistModal from "../components/SetupChecklistModal";
import ProvisioningPanel from "../components/ProvisioningPanel";
import AIEmployeesPreview from "../components/AIEmployeesPreview";
import AssistantSection from "../components/AssistantSection";
import ResourceAssignChips from "../components/ResourceAssignChips";
import { RetailOverviewContent } from "./RetailOverview";
import RetailReadinessTile from "../components/RetailReadinessTile";
import { fireMilestone, MILESTONES } from "../lib/onboardingMilestones";

const StatCard = ({ icon: Icon, label, value, suffix, hint, accent }) => (
  <Card className="p-5 hover:shadow-sm transition" data-testid={`overview-stat-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
      <Icon className={`w-3.5 h-3.5 ${accent || ""}`}/>{label}
    </div>
    <div className="font-display text-2xl sm:text-3xl font-semibold mt-1 tabular-nums">
      {value}
      {suffix && <span className="text-sm text-muted-foreground ml-1 font-normal">{suffix}</span>}
    </div>
    {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
  </Card>
);

const EmptyHint = ({ children, testid }) => (
  <div className="text-sm text-muted-foreground italic flex items-center gap-2 px-1" data-testid={testid}>
    <Sparkles className="w-3.5 h-3.5"/>{children}
  </div>
);

// Phase 1 Customer Foundation (Feb 2026) — lifecycle counts strip.
// Sits above the existing stat row on the Overview page. Fetches
// `/api/customers/lifecycle-counts` once on mount.
const LifecycleStrip = () => {
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    api.get("/customers/lifecycle-counts")
      .then(r => setCounts(r.data))
      .catch(() => { /* tenant may not have customers yet — silent */ });
  }, []);
  if (!counts) return null;
  const cells = [
    { key: "leads",               label: "Leads",     to: "/dashboard/leads" },
    { key: "prospects",           label: "Prospects", to: "/dashboard/leads?lead_status=prospect" },
    { key: "customers",           label: "Customers", to: "/dashboard/customers" },
    { key: "existing_customers",  label: "Existing",  to: "/dashboard/customers?stage=existing_customer" },
    { key: "inactive_customers",  label: "Inactive",  to: "/dashboard/customers?stage=inactive_customer" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="dashboard-lifecycle-strip">
      {cells.map(c => (
        <Link key={c.key} to={c.to} className="block">
          <Card className="p-4 hover:shadow-sm transition cursor-pointer" data-testid={`lifecycle-stat-${c.key}`}>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{c.label}</div>
            <div className="text-2xl font-semibold mt-1 tabular-nums">{counts[c.key] ?? 0}</div>
          </Card>
        </Link>
      ))}
    </div>
  );
};

/**
 * v1.0.17 — Industry-aware feature toggle set.
 *
 * Each tenant industry sees only the toggles relevant to its customer base.
 * Restaurant tenants no longer see RCM / ProCare-IT / competitor-intel
 * toggles; healthcare practices no longer see catering / takeout toggles.
 * Unknown industries get a neutral baseline.
 *
 * Toggle IDs map 1:1 to the backend `ALLOWED_FEATURE_FLAGS` allow-list so
 * `feature_flags: { id: bool }` round-trips cleanly through PUT /chatbot.
 */
const RESTAURANT_FLAGS = [
  { id: "answer_menu_questions",     label: "Menu questions" },
  { id: "answer_catering_inquiries", label: "Catering inquiries" },
  { id: "answer_takeout_inquiries",  label: "To-go / takeout order inquiries" },
  { id: "answer_specials_offers",    label: "Specials & offers questions" },
  { id: "answer_hours_location",     label: "Business hours & location" },
  { id: "capture_contact_callback",  label: "Capture contact / callback" },
  { id: "voice_enabled",             label: "Voice replies (TTS)" },
];
const HEALTHCARE_FLAGS = [
  { id: "voice_enabled",           label: "Voice replies (TTS)" },
  { id: "pricing_modal",           label: "Pricing intake modal" },
  { id: "rcm_intake_modal",        label: "RCM / billing intake modal" },
  { id: "competitor_intelligence", label: "Use competitor knowledge in answers" },
  { id: "procare_it",              label: "ProCare IT / cybersecurity add-on" },
  { id: "answer_hours_location",   label: "Office hours & location" },
  { id: "capture_contact_callback",label: "Capture contact / callback" },
];
const NEUTRAL_FLAGS = [
  { id: "answer_hours_location",    label: "Share business info (hours, location)" },
  { id: "capture_contact_callback", label: "Collect contact details / callback" },
  { id: "voice_enabled",            label: "Voice replies (TTS)" },
];

function industryFeatureFlags(industry) {
  const s = (industry || "").toLowerCase();
  if (/(restaurant|food|cafe|cuisine|catering|bakery|pizza|deli)/.test(s)) return RESTAURANT_FLAGS;
  if (/(health|clinic|dental|medic|pharma|hospital|practice)/.test(s)) return HEALTHCARE_FLAGS;
  return NEUTRAL_FLAGS;
}

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    const secs = Math.max(0, (Date.now() - dt.getTime()) / 1000);
    if (secs < 60) return "just now";
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
    return dt.toLocaleDateString();
  } catch (_e) { return "—"; }
}

const STATUS_COLORS = {
  High: "bg-emerald-500", Medium: "bg-amber-500", Marketing: "bg-sky-500",
  Lost: "bg-rose-400", Junk: "bg-stone-400",
};
const STATUS_BADGES = {
  High: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Medium: "bg-amber-100 text-amber-800 border-amber-200",
  Marketing: "bg-sky-100 text-sky-800 border-sky-200",
  Lost: "bg-rose-100 text-rose-700 border-rose-200",
  Junk: "bg-stone-100 text-stone-700 border-stone-200",
};

// Shared Overview cards. Rendered by both the default Overview and the
// Retail Operations Overview, so neither duplicates the other's markup.
function PublicLinkCard({ chatbot, link, pub }) {
  return (
    <Card className="p-5 mb-6 bg-gradient-to-br from-accent/5 to-transparent border-accent/20" data-testid="public-link-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-1.5">
            <Globe className="w-3.5 h-3.5"/> Public assistant link
            {pub.status === "active" && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full"><span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"/>LIVE</span>}
          </div>
          <div className="font-mono text-sm bg-secondary/60 px-3 py-2 rounded-lg break-all" data-testid="public-link">{link}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }} data-testid="copy-link-btn"><Copy className="w-3.5 h-3.5 mr-1.5"/>Copy</Button>
          <Link to={`/chat/${chatbot.slug}`} target="_blank"><Button size="sm" data-testid="open-link-btn"><ExternalLink className="w-3.5 h-3.5 mr-1.5"/>Open</Button></Link>
          <Link to="/dashboard/share"><Button size="sm" variant="ghost" data-testid="share-link-btn">Share & QR <ChevronRight className="w-3.5 h-3.5 ml-1"/></Button></Link>
        </div>
      </div>
    </Card>
  );
}

function AssistantActivityCard({ overview, className = "lg:col-span-2 p-5" }) {
  const hasData = overview?.has_data || {};
  const ts = overview?.conversation_timeseries || [];
  const tsMax = Math.max(1, ...ts.map((d) => d.count));
  return (
    <Card className={className} data-testid="performance-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="font-display text-lg font-semibold">Assistant activity</h2>
          <p className="text-xs text-muted-foreground">Conversations · last 14 days</p>
        </div>
        {hasData.timeseries && (
          <span className="text-xs text-muted-foreground tabular-nums">{ts.reduce((s, d) => s + d.count, 0)} total</span>
        )}
      </div>
      {hasData.timeseries ? (
        <div className="grid grid-cols-14 gap-1 items-end h-32 mt-3" style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }} data-testid="timeseries-chart">
          {ts.map((d) => {
            const h = Math.max(4, Math.round((d.count / tsMax) * 100));
            return (
              <div key={d.date} className="flex flex-col items-center justify-end h-full group">
                <div className="w-full bg-accent/80 rounded-t group-hover:bg-accent transition" style={{ height: `${h}%` }} title={`${d.date}: ${d.count}`}/>
                <div className="text-[9px] text-muted-foreground mt-1">{d.label[0]}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="h-32 grid place-items-center" data-testid="performance-empty">
          <EmptyHint testid="performance-empty-hint">Engagement insights will appear as conversations increase.</EmptyHint>
        </div>
      )}
    </Card>
  );
}

function RecentConversationsCard({ overview }) {
  const recentConvos = overview?.recent_conversations || [];
  return (
    <Card className="p-5" data-testid="recent-convos-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-semibold">Recent conversations</h2>
        <Link to="/dashboard/conversations" className="text-xs text-muted-foreground hover:text-foreground" data-testid="recent-convos-all">View all <ChevronRight className="inline w-3 h-3"/></Link>
      </div>
      {recentConvos.length > 0 ? (
        <ul className="divide-y divide-border">
          {recentConvos.map((c) => (
            <li key={c.id} className="py-2.5" data-testid={`recent-convo-${c.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="font-medium text-sm truncate flex-1">{c.title}</div>
                <span className="text-[11px] text-muted-foreground shrink-0">{timeAgo(c.updated_at)}</span>
              </div>
              <div className="text-xs text-muted-foreground truncate mt-0.5">{c.preview || "—"}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] text-muted-foreground">{c.turn_count} turn{c.turn_count === 1 ? "" : "s"}</span>
                {c.lead_captured && <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded"><CheckCircle2 className="w-2.5 h-2.5"/>Lead captured</span>}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="grid place-items-center py-8" data-testid="recent-convos-empty">
          <EmptyHint>Conversation history will appear once visitors start chatting.</EmptyHint>
        </div>
      )}
    </Card>
  );
}

// Retail Reporting Sprint 1 (Oct 2026) — the SERVER decides which Overview
// a tenant gets (`GET /company` -> `dashboard_experience`, resolved by
// `backend/services/dashboard_experience.py`). Retail Operations tenants get
// `RetailOperationsOverview`; every other tenant gets `DefaultOverview`
// below, unchanged. A short neutral placeholder covers the one request, so a
// retail tenant never sees a flash of the lead dashboard.
export function Overview() {
  const [experience, setExperience] = useState(null);
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/company")
      .then((r) => { if (alive) { setInfo(r?.data || null); setExperience(r?.data?.dashboard_experience || "default"); } })
      .catch(() => { if (alive) setExperience("default"); });
    return () => { alive = false; };
  }, []);
  if (experience === null) {
    return (
      <DashboardLayout>
        <div className="h-40 animate-pulse rounded-xl bg-secondary/60" aria-label="Loading overview" data-testid="overview-loading" />
      </DashboardLayout>
    );
  }
  if (experience === "retail_operations") {
    return <RetailOperationsOverview info={info} />;
  }
  return <DefaultOverview />;
}

// Retail Operations Overview (Oct 2026 composition correction). Retail mode
// keeps the Sales Assistant: setup and readiness first, then the Retail
// Operations reporting, then the assistant's link and activity. The B2B-only
// sections (lifecycle strip, lead KPI row, Lead pipeline, Recent leads) and
// the AI Employees preview are not rendered here.
function RetailOperationsOverview({ info }) {
  const [overview, setOverview] = useState(null);
  useEffect(() => {
    api.get("/dashboard/overview").then(r => setOverview(r.data)).catch(() => {});
  }, []);
  const link = info?.chatbot ? `${window.location.origin}/chat/${info.chatbot.slug}` : "";
  const pub = overview?.public_assistant || {};
  return (
    <DashboardLayout>
      <ProvisioningPanel />
      <RetailReadinessTile />
      <div className="mb-6">
        <RetailOverviewContent />
      </div>
      {info?.chatbot && <PublicLinkCard chatbot={info.chatbot} link={link} pub={pub} />}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <AssistantActivityCard overview={overview} className="p-5" />
        <RecentConversationsCard overview={overview} />
      </div>
    </DashboardLayout>
  );
}

function DefaultOverview() {
  const [overview, setOverview] = useState(null);
  const [info, setInfo] = useState(null);
  const [showcase, setShowcase] = useState(null);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const fetchShowcase = () => api.get("/dashboard/showcase").then(r => setShowcase(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/dashboard/overview").then(r => setOverview(r.data)).catch(() => {});
    api.get("/company").then(r => setInfo(r.data));
    fetchShowcase();
  }, []);
  const link = info?.chatbot ? `${window.location.origin}/chat/${info.chatbot.slug}` : "";
  const company = info?.company || {};
  const chatbot = info?.chatbot || {};
  const isDemoTenant = Boolean(showcase?.is_demo_tenant);

  // Polished Gold-Demo replacement view for the seeded healthcare tenant.
  if (isDemoTenant && showcase) {
    return (
      <DashboardLayout>
        <ShowcaseDashboard
          data={showcase}
          link={link}
          slug={chatbot.slug}
          isReadOnly={showcase.is_read_only}
          viewerRole={showcase.viewer_role}
          onReset={fetchShowcase}
        />
      </DashboardLayout>
    );
  }

  const stats = overview?.stats || {};
  const hasData = overview?.has_data || {};
  const recentLeads = overview?.recent_leads || [];
  const leadBreakdown = overview?.lead_breakdown || [];
  const readiness = overview?.resource_readiness || {};
  const pub = overview?.public_assistant || {};

  // Setup checklist — booleans derived from real state (now backing the modal).
  const docsCount = stats.resources || 0;
  const steps = [
    { id: "profile",  label: "Complete business profile",         done: Boolean(company.description || company.address || company.business_hours), to: "/dashboard/profile" },
    { id: "website",  label: "Import your website",                done: docsCount > 0,                             to: "/dashboard/resources" },
    { id: "docs",     label: "Upload PDFs / spreadsheets",         done: docsCount > 0,                             to: "/dashboard/resources" },
    { id: "branding", label: "Customise chatbot branding",         done: Boolean(chatbot.logo_url) || Boolean(chatbot.theme_color && chatbot.theme_color !== "#E8A33D"), to: "/dashboard/chatbot" },
    { id: "test",     label: "Test your chatbot",                  done: (stats.conversations || 0) > 0,           to: link, external: Boolean(link) },
    { id: "share",    label: "Share the link / QR code",            done: false,                                    to: "/dashboard/share" },
  ];
  const stepsDone = steps.filter(s => s.done).length;
  const stepsPct = Math.round((stepsDone / steps.length) * 100);

  const trialEnd = company.trial_end ? new Date(company.trial_end) : null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000)) : null;
  const statusLabel = { active: "Active", draft: "Draft", not_configured: "Not configured" }[pub.status] || "—";
  const statusAccent = pub.status === "active" ? "text-emerald-600" : pub.status === "draft" ? "text-amber-600" : "text-muted-foreground";

  return (
    <DashboardLayout>
      {/* Slice C (Feb 2026) — Provisioning Panel. Renders ONLY when the tenant
          is mid-pipeline (requires_provisioning && status != "ready"). Returns
          null for all existing/legacy tenants so the dashboard looks identical
          for them. */}
      <ProvisioningPanel />
      {/* Phase 3G.2 — Retail Readiness Tile. Self-hides for non-retail tenants. */}
      <RetailReadinessTile />
      {/* Hero strip */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold" data-testid="overview-title">
            {(company.display_name || company.name) ? `Welcome back, ${company.display_name || company.name}` : "Welcome back"}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Your AI assistant control center.
            {company.plan_status === "trial" && trialEnd && (
              <span className="ml-2 text-xs bg-accent/15 text-accent-foreground px-2 py-0.5 rounded-full" data-testid="trial-pill">Trial · {daysLeft} day{daysLeft === 1 ? "" : "s"} left</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {stepsDone < steps.length && (
            <Button variant="outline" size="sm" onClick={() => setChecklistOpen(true)} data-testid="open-checklist-btn">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5"/> Setup checklist
              <span className="ml-2 text-xs px-1.5 py-0.5 bg-muted rounded-full tabular-nums">{stepsDone}/{steps.length}</span>
            </Button>
          )}
        </div>
      </div>

      <UsageBanner />

      {/* Phase 1 Customer Foundation (Feb 2026) — lifecycle strip. */}
      <LifecycleStrip />

      {/* Executive Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
        <StatCard icon={MessagesSquare} label="Conversations" value={stats.conversations ?? "—"} hint={hasData.conversations ? `${stats.messages || 0} messages` : "No traffic yet"}/>
        <StatCard icon={Users} label="Leads captured" value={stats.leads ?? "—"} hint={hasData.leads ? `Across pipeline` : "None captured yet"}/>
        <StatCard icon={FileText} label="Resources" value={stats.resources ?? "—"} hint={readiness.indexed ? `${readiness.chunks} indexed` : "Add knowledge to get started"}/>
        <StatCard icon={Bot} label="Usage" value={`${stats.usage_percent ?? 0}%`} hint="of monthly limit"/>
        <StatCard icon={BarChart3} label="Est. cost" value={`$${stats.estimated_cost_usd ?? "0"}`} hint="This month"/>
        <StatCard icon={Globe} label="Public assistant" value={statusLabel} hint={pub.slug ? `/chat/${pub.slug}` : "Configure in Chatbot →"} accent={statusAccent}/>
      </div>

      {/* Public link panel */}
      {info?.chatbot && <PublicLinkCard chatbot={info.chatbot} link={link} pub={pub} />}

      {/* Assistant Performance & Lead funnel */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {/* Conversations-over-time spark */}
        <AssistantActivityCard overview={overview} />

        {/* Lead breakdown */}
        <Card className="p-5" data-testid="lead-breakdown-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-semibold">Lead pipeline</h2>
            <Link to="/dashboard/leads" className="text-xs text-muted-foreground hover:text-foreground" data-testid="leads-page-link">All leads <ChevronRight className="inline w-3 h-3"/></Link>
          </div>
          {hasData.leads ? (
            <div className="space-y-2.5">
              {leadBreakdown.map((b) => {
                const max = Math.max(1, ...leadBreakdown.map((x) => x.count));
                const w = Math.max(4, Math.round((b.count / max) * 100));
                return (
                  <div key={b.status}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium">{b.status}</span>
                      <span className="tabular-nums text-muted-foreground">{b.count}</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full ${STATUS_COLORS[b.status]} transition-all duration-500`} style={{ width: `${w}%` }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid place-items-center py-6" data-testid="lead-breakdown-empty">
              <EmptyHint>No leads captured yet — they'll appear here after your first qualifying chat.</EmptyHint>
            </div>
          )}
        </Card>
      </div>

      {/* Recent activity (leads + convos) */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <Card className="p-5" data-testid="recent-leads-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-semibold">Recent leads</h2>
            <Link to="/dashboard/leads" className="text-xs text-muted-foreground hover:text-foreground" data-testid="recent-leads-all">View all <ChevronRight className="inline w-3 h-3"/></Link>
          </div>
          {recentLeads.length > 0 ? (
            <ul className="divide-y divide-border">
              {recentLeads.map((l) => (
                <li key={l.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`recent-lead-${l.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{l.lead_name || l.name || "Unnamed lead"}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {l.email || l.phone || l.company || l.product || "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {l.status && <span className={`text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full border ${STATUS_BADGES[l.status] || "bg-muted"}`}>{l.status}</span>}
                    <span className="text-[11px] text-muted-foreground">{timeAgo(l.created_at || l.date_added)}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="grid place-items-center py-8" data-testid="recent-leads-empty">
              <EmptyHint>Captured leads will appear here as your assistant qualifies prospects.</EmptyHint>
            </div>
          )}
        </Card>

        <RecentConversationsCard overview={overview} />
      </div>

      {/* Slice M+ (Feb 2026) — AI Employees preview. Lightweight teaser only;
          full management UI ships in a later slice. */}
      <AIEmployeesPreview />

      {/* Resource readiness */}
      <Card className="p-5 mb-6" data-testid="resource-readiness-card">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-display text-lg font-semibold">Knowledge base readiness</h2>
            <p className="text-xs text-muted-foreground">Your assistant is only as good as the resources it's trained on.</p>
          </div>
          <Link to="/dashboard/resources"><Button size="sm" variant="outline" data-testid="resources-page-link"><Upload className="w-3.5 h-3.5 mr-1.5"/>Manage resources</Button></Link>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="p-3 rounded-lg bg-muted/40">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Documents</div>
            <div className="font-display text-2xl font-semibold tabular-nums">{readiness.documents ?? 0}</div>
          </div>
          <div className="p-3 rounded-lg bg-muted/40">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Indexed chunks</div>
            <div className="font-display text-2xl font-semibold tabular-nums">{readiness.chunks ?? 0}</div>
          </div>
          <div className="p-3 rounded-lg bg-muted/40">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last upload</div>
            <div className="text-sm font-medium truncate">{readiness.last_resource_name || "—"}</div>
            <div className="text-[11px] text-muted-foreground">{readiness.last_resource_at ? timeAgo(readiness.last_resource_at) : "—"}</div>
          </div>
        </div>
        {!hasData.resources && (
          <div className="mt-3" data-testid="resource-readiness-empty">
            <EmptyHint>Upload a few PDFs, spreadsheets, or import your website to give the assistant context.</EmptyHint>
          </div>
        )}
      </Card>

      {/* Setup checklist nudge for new tenants */}
      {stepsDone < steps.length && (
        <Card className="p-5 mb-4 bg-gradient-to-br from-amber-50/60 to-transparent border-amber-200" data-testid="setup-nudge">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-amber-600"/>
                <h3 className="font-medium">Finish setting up your assistant</h3>
                <span className="text-xs tabular-nums px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full">{stepsPct}%</span>
              </div>
              <p className="text-xs text-muted-foreground">{steps.length - stepsDone} step{steps.length - stepsDone === 1 ? "" : "s"} remaining — most customers are live in under 15 minutes.</p>
            </div>
            <Button size="sm" onClick={() => setChecklistOpen(true)} data-testid="overview-view-checklist">View setup checklist <ChevronRight className="w-3.5 h-3.5 ml-1"/></Button>
          </div>
        </Card>
      )}

      <SetupChecklistModal open={checklistOpen} onOpenChange={setChecklistOpen} steps={steps} link={link}/>
    </DashboardLayout>
  );
}

// The canonical tenant/location timezone control (Phase 0, Sep 2026;
// converted from a free-text box to a select later the same month).
//
// Two things happen here that a bare dropdown would not do:
//   * a live sample of the current time in the selected zone, so a
//     mis-click between two plausible neighbours is caught by eye before
//     saving;
//   * the backend's OWN resolution is displayed when the canonical field
//     is empty, so a tenant relying on the transitional printing-config
//     fallback or on the legacy default can SEE that, rather than
//     discovering it when fulfillment hours behave unexpectedly. The
//     backend never backfills this field — establishing it is always a
//     deliberate tenant action.
function BusinessTimezoneField({ value, onChange }) {
  const [resolved, setResolved] = useState(null);
  const current = String(value || "");
  // Kept from the free-text era for the live sample only. A `<select>`
  // cannot emit an unsupported value, so `timezoneProblem` can now only
  // fire for a legacy value already in the record — which is worth
  // showing rather than hiding.
  const problem = timezoneProblem(current);
  const sample = problem || !current ? null : timezoneSample(current);
  // Sep 2026 — the choices are FETCHED, never hard-coded here. The same
  // `/company/scheduling-timezone` call that already reported what
  // scheduling resolves now also carries the supported-value list, from
  // `services/scheduling/timezones.py` — the module that resolves the
  // zone AND backs `PUT /api/company`'s validation. So this page holds
  // no timezone knowledge of its own: no label->IANA table to drift, no
  // offsets, nothing to reinterpret.
  const options = resolved?.options || [];

  useEffect(() => {
    let alive = true;
    api.get("/company/scheduling-timezone")
      .then(({ data: r }) => { if (alive) setResolved(r || null); })
      // Purely informational — a failure here must never block the
      // profile form or surface a toast the tenant cannot act on.
      .catch(() => { if (alive) setResolved(null); });
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="bp-timezone-field">
      <Label htmlFor="bp-timezone">Time zone</Label>
      {/* Sep 2026 — was a free-text IANA box, which is how a restaurant
          owner ends up typing "EST" (a real IANA zone that never becomes
          EDT). The stored value is still a canonical IANA identifier —
          that is what makes daylight saving automatic — but nobody has
          to know the database to pick one. A saved zone outside the
          offered list is appended by the endpoint, so switching to a
          select cannot silently drop or change what is stored. */}
      <select
        id="bp-timezone"
        data-testid="bp-timezone"
        className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        value={current}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Select your time zone…</option>
        {options.map((tz) => (
          <option key={tz.value} value={tz.value}>{tz.label}</option>
        ))}
        {/* A stored value the endpoint has not returned yet (first paint,
            or a failed fetch) still renders, so the control never shows
            blank for a tenant who has one configured. */}
        {current && !options.some((tz) => tz.value === current) && (
          <option value={current}>{current}</option>
        )}
      </select>
      <div className="text-xs mt-1 space-y-0.5">
        <p className="text-muted-foreground">
          Used for order scheduling, fulfillment hours, kitchen and order timestamps,
          coupon reminders and receipt timestamps. Daylight saving is applied automatically.
        </p>
        {problem && (
          <p className="text-red-600" data-testid="bp-timezone-problem">{problem}</p>
        )}
        {!problem && current && sample && (
          <p className="text-muted-foreground" data-testid="bp-timezone-sample">
            Right now in {current}: {sample}
          </p>
        )}
        {!current && resolved && !resolved.is_canonical && (
          <p className="text-amber-700" data-testid="bp-timezone-unset">
            {resolved.is_configured
              ? `Not set. Scheduling currently falls back to your receipt time zone (${resolved.timezone}). Set it here to make it official.`
              : `Not set. Scheduling currently assumes ${resolved.timezone}. Set your real time zone before enabling fulfillment scheduling.`}
          </p>
        )}
      </div>
    </div>
  );
}

export function BusinessProfile() {
  const [data, setData] = useState({});
  const [baseline, setBaseline] = useState({});
  const [saving, setSaving] = useState(false);
  // v1.0.18 — synchronous double-submit guard. `setSaving(true)` is scheduled
  // by React and doesn't land until the next render, so 5 rapid clicks all
  // see saving=false and fire 5 PUTs. A ref mutates synchronously and blocks
  // re-entry on the same tick.
  const savingRef = useRef(false);
  useEffect(() => {
    api.get("/company").then(r => {
      const co = r.data.company || {};
      // Tenant identity model (Aug 2026) — `display_name` is the
      // tenant-editable business-facing name; `name` (Registered Company
      // Name, set at signup) is read-only here. Legacy tenants from before
      // `display_name` existed fall back to `name` so the field is never
      // blank; saving naturally backfills it going forward.
      const withDisplayName = { ...co, display_name: co.display_name || co.name || "" };
      setData(withDisplayName);
      setBaseline(withDisplayName);
    });
  }, []);
  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    // Auto-prepend https:// to bare/`www.` website inputs so the UX matches user expectation.
    const payload = { ...data, website: normalizeWebsiteUrl(data.website) };
    try {
      const { data: resp } = await api.put("/company", payload);
      // Reflect normalized values (e.g. website) back into the form.
      const saved = resp?.company || payload;
      setData(saved);
      setBaseline(saved);     // v1.0.16 — clear dirty state after a save.
      toast.success("Profile saved");
    } catch { toast.error("Save failed"); } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  const set = (k, v) => setData({...data, [k]: v});
  // v1.0.16 — unsaved-changes guard. Only fires when the form actually differs.
  const isDirty = JSON.stringify(data) !== JSON.stringify(baseline);
  useUnsavedChangesGuard(isDirty);
  return (
    <DashboardLayout>
      <h1 className="font-display text-4xl font-semibold mb-2">Business Profile</h1>
      <p className="text-muted-foreground mb-8">This information appears in your chatbot.</p>
      <Card className="p-6 space-y-4 max-w-2xl">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Registered Company Name</Label>
            <Input data-testid="bp-registered-name" className="mt-1.5" value={data.name || ""} disabled readOnly/>
            <p className="text-xs text-muted-foreground mt-1">Set during registration. This account name cannot be changed from Business Profile.</p>
          </div>
          <div>
            <Label>Display Name</Label>
            <Input data-testid="bp-name" className="mt-1.5" value={data.display_name || ""} onChange={e => set("display_name", e.target.value)}/>
            <p className="text-xs text-muted-foreground mt-1">Shown throughout Rudras-AI where your business name is displayed. Changing this does not change your public links.</p>
          </div>
        </div>
        <div><Label>Industry</Label><Input data-testid="bp-industry" className="mt-1.5" value={data.industry || ""} onChange={e => set("industry", e.target.value)} placeholder="restaurant"/></div>
        <div><Label>Description</Label><Textarea data-testid="bp-desc" className="mt-1.5" rows={3} value={data.description || ""} onChange={e => set("description", e.target.value)}/></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Contact email</Label><Input data-testid="bp-email" className="mt-1.5" value={data.contact_email || ""} onChange={e => set("contact_email", e.target.value)}/></div>
          <div><Label>Phone</Label><Input data-testid="bp-phone" className="mt-1.5" value={data.phone || ""} onChange={e => set("phone", e.target.value)}/></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Website</Label>
            <Input
              data-testid="bp-website"
              className="mt-1.5"
              value={data.website || ""}
              onChange={e => set("website", e.target.value)}
              onBlur={e => set("website", normalizeWebsiteUrl(e.target.value))}
              placeholder="yourbusiness.com"
            />
          </div>
          <div><Label>Business hours</Label><Input data-testid="bp-hours" className="mt-1.5" value={data.business_hours || ""} onChange={e => set("business_hours", e.target.value)}/></div>
        </div>
        <div><Label>Address</Label><Input data-testid="bp-address" className="mt-1.5" value={data.address || ""} onChange={e => set("address", e.target.value)}/></div>
        <BusinessTimezoneField value={data.timezone} onChange={(v) => set("timezone", v)} />
        <div>
          <Label>Logo</Label>
          <div className="mt-1.5">
            <LogoUploader
              testIdPrefix="bp"
              value={data.logo_url || ""}
              onChange={(url) => set("logo_url", url)}
            />
          </div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="bp-save" className="bg-primary hover:bg-primary/90">{saving ? "Saving..." : "Save profile"}</Button>
      </Card>
    </DashboardLayout>
  );
}

// ── Premium Voice Optimization v1 — Voice settings panel + Preview Voice button.
// Lives inside Branding/ChatbotSettings (lazy-loaded as a section, not its own tab,
// so customers don't have to hunt for it). Preview is server-cached → repeat clicks
// on the same voice cost nothing.
function VoiceSettingsSection({ bot, set }) {
  const [previewing, setPreviewing] = useState(false);
  const [lastPreviewKey, setLastPreviewKey] = useState(null);
  const audioRef = useRef(null);
  const VOICES = [
    { id: "nova",    label: "Nova — calm female (executive)" },
    { id: "shimmer", label: "Shimmer — warm female" },
    { id: "coral",   label: "Coral — bright female" },
    { id: "sage",    label: "Sage — measured neutral" },
    { id: "alloy",   label: "Alloy — balanced neutral" },
    { id: "echo",    label: "Echo — calm male" },
    { id: "onyx",    label: "Onyx — authoritative male" },
    { id: "ash",     label: "Ash — articulate male" },
    { id: "fable",   label: "Fable — expressive neutral" },
  ];
  const STYLES = [
    { id: "executive_calm",    label: "Executive calm — deliberate" },
    { id: "warm_consultative", label: "Warm consultative — advisory" },
    { id: "energetic",         label: "Energetic — upbeat" },
    { id: "neutral",           label: "Neutral — no styling" },
  ];

  const stopPreview = () => {
    try { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } } catch (_) {}
    setPreviewing(false);
  };

  const playPreview = async () => {
    stopPreview();
    setPreviewing(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chatbot/voice/preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({
          voice_profile: bot.voice_profile || "nova",
          voice_gender: bot.voice_gender || "female",
          speech_style: bot.speech_style || "executive_calm",
          speaking_speed: Number(bot.speaking_speed) || 0.96,
          assistant_name: bot.assistant_name || "Zara",
        }),
      });
      if (!resp.ok) {
        // Try to surface the backend's reason for the failure so the
        // customer sees an actionable hint rather than a generic toast.
        let detail = "";
        try {
          const json = await resp.json();
          detail = (json?.detail || "").toString();
        } catch (_) { /* response wasn't JSON — that's fine */ }
        const provider = (bot.voice_provider || "openai");
        toast.error(
          detail
            ? `Voice preview failed: ${detail}`
            : `Voice preview from ${provider} didn't return audio — try a different voice or speed, or check your network.`
        );
        setPreviewing(false);
        return;
      }
      const cacheHeader = resp.headers.get("x-voice-cache");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { stopPreview(); URL.revokeObjectURL(url); };
      audio.onerror = () => { stopPreview(); URL.revokeObjectURL(url); };
      setLastPreviewKey(`${bot.voice_profile}-${bot.speaking_speed}-${bot.assistant_name}-${cacheHeader}`);
      audio.play().catch(() => stopPreview());
    } catch (_) {
      stopPreview();
      toast.error("Preview failed");
    }
  };

  return (
    <div className="border rounded-lg p-4 space-y-4 bg-muted/20" data-testid="voice-settings-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold">Voice <span className="text-xs text-muted-foreground font-normal">— premium TTS, cached server-side</span></div>
          <div className="text-xs text-muted-foreground">Control how your assistant SOUNDS on voice-enabled chats. Repeat replies (greetings, pricing, hours) are cached automatically — visitors hear instant playback after the first synth.</div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm whitespace-nowrap">
          <input
            type="checkbox"
            data-testid="cb-voice-enabled"
            checked={!!bot.voice_enabled}
            onChange={e => set("voice_enabled", e.target.checked)}
            className="h-4 w-4"
          />
          Voice enabled
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Voice profile</Label>
          <select
            data-testid="cb-voice-profile"
            className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={bot.voice_profile || "nova"}
            onChange={e => set("voice_profile", e.target.value)}
          >
            {VOICES.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
          <p className="text-xs text-muted-foreground mt-1">9 OpenAI TTS voices. Nova is the calm executive default.</p>
        </div>
        <div>
          <Label>Speech style</Label>
          <select
            data-testid="cb-voice-style"
            className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={bot.speech_style || "executive_calm"}
            onChange={e => set("speech_style", e.target.value)}
          >
            {STYLES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <p className="text-xs text-muted-foreground mt-1">Subtle pacing + silence-threshold differences.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label>Voice gender</Label>
          <select
            data-testid="cb-voice-gender"
            className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={bot.voice_gender || "female"}
            onChange={e => set("voice_gender", e.target.value)}
          >
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="neutral">Neutral</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <Label>Speaking speed <span className="text-xs text-muted-foreground">({Number(bot.speaking_speed || 0.96).toFixed(2)}×)</span></Label>
          <input
            type="range" min="0.7" max="1.3" step="0.02"
            data-testid="cb-voice-speed"
            value={Number(bot.speaking_speed || 0.96)}
            onChange={e => set("speaking_speed", parseFloat(e.target.value))}
            className="mt-3 w-full"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground"><span>0.70× — deliberate</span><span>1.00×</span><span>1.30× — brisk</span></div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button
          type="button"
          onClick={previewing ? stopPreview : playPreview}
          variant="outline"
          data-testid="cb-voice-preview"
        >
          {previewing ? "Stop preview" : "Preview voice"}
        </Button>
        <div className="text-xs text-muted-foreground">
          Plays a short 25-word sample using your current settings. Cached — repeat clicks are free.
          {lastPreviewKey?.endsWith("hit") && <span className="ml-2 text-emerald-700">⚡ instant (cache hit)</span>}
        </div>
      </div>
    </div>
  );
}


export function ChatbotSettings() {
  const [bot, setBot] = useState({});
  const [baseline, setBaseline] = useState({});
  const [industry, setIndustry] = useState("");
  const [saving, setSaving] = useState(false);
  // v1.0.18 — synchronous double-submit guard (see BusinessProfile rationale).
  const savingRef = useRef(false);
  useEffect(() => {
    api.get("/company").then(r => {
      const cb = r.data.chatbot || {};
      setBot(cb);
      setBaseline(cb);
      // v1.0.17 — industry drives which feature toggles are shown below.
      setIndustry((r.data.company?.industry || "").toLowerCase());
    });
  }, []);
  const set = (k, v) => setBot(prev => ({ ...prev, [k]: v }));
  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await api.put("/chatbot", bot);
      setBaseline(bot);       // v1.0.16 — clear dirty state after a save.
      toast.success("Branding saved");
    }
    catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  // v1.0.16 — unsaved-changes guard.
  const isDirty = JSON.stringify(bot) !== JSON.stringify(baseline);
  useUnsavedChangesGuard(isDirty);

  const previewBg = bot.theme_color || "#E8A33D";
  const logoSrc = bot.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${bot.logo_url}` : "";

  // Curated palette helps customers avoid the "type a random hex code" hesitation.
  const palette = ["#E8A33D", "#0A1F3D", "#2563EB", "#059669", "#DC2626", "#7C3AED", "#EA580C", "#0891B2", "#111827"];

  return (
    <DashboardLayout>
      <h1 className="font-display text-4xl font-semibold mb-2">Branding</h1>
      <p className="text-muted-foreground mb-8">Make the chatbot feel like part of your business — logo, colours, welcome message, and the small print.</p>
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 space-y-5 lg:col-span-2" data-testid="branding-form">
          {/* Logo */}
          <div>
            <Label>Logo</Label>
            <div className="mt-1.5">
              <LogoUploader
                testIdPrefix="branding"
                value={bot.logo_url || ""}
                onChange={(url) => set("logo_url", url)}
              />
            </div>
          </div>

          {/* Chatbot name */}
          <div>
            <Label>Chatbot name</Label>
            <Input data-testid="branding-name" className="mt-1.5" value={bot.name || ""} onChange={e => set("name", e.target.value)} placeholder="e.g. Acme Assistant"/>
          </div>

          {/* Phase 1 — Assistant identity (name + personality) */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Assistant name <span className="text-xs text-muted-foreground">(spoken identity)</span></Label>
              <Input
                data-testid="cb-assistant-name"
                className="mt-1.5"
                maxLength={40}
                value={bot.assistant_name || ""}
                onChange={e => set("assistant_name", e.target.value)}
                placeholder="e.g. Nova, Riley, Atlas — defaults to 'Assistant'"
              />
              <p className="text-xs text-muted-foreground mt-1">The name your AI introduces itself with. Different from the chatbot's display name above.</p>
            </div>
            <div>
              <Label>Personality style</Label>
              <select
                data-testid="cb-personality-style"
                className="mt-1.5 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={bot.personality_style || ""}
                onChange={e => set("personality_style", e.target.value)}
              >
                <option value="">— Default (friendly) —</option>
                <option value="friendly">Friendly — warm & conversational</option>
                <option value="professional">Professional — calm & precise</option>
                <option value="executive">Executive — concise & confident</option>
                <option value="consultative">Consultative — advisory tone</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">Shapes how the AI greets visitors and varies its phrasing.</p>
            </div>
          </div>

          {/* Premium Voice Layer v1 — voice identity */}
          <VoiceSettingsSection bot={bot} set={set} />

          {/* Welcome message */}
          <div>
            <Label>Welcome message</Label>
            <Textarea data-testid="cb-welcome" rows={2} className="mt-1.5" value={bot.welcome_message || ""} onChange={e => set("welcome_message", e.target.value)}/>
          </div>

          {/* Theme colour */}
          <div>
            <Label>Theme colour</Label>
            <div className="flex flex-wrap gap-2 mt-1.5 items-center">
              <Input type="color" data-testid="cb-color" value={bot.theme_color || "#E8A33D"} onChange={e => set("theme_color", e.target.value)} className="w-14 h-10 p-1 cursor-pointer"/>
              <Input value={bot.theme_color || ""} onChange={e => set("theme_color", e.target.value)} placeholder="#E8A33D" className="w-32"/>
              <div className="flex flex-wrap gap-1.5 ml-1">
                {palette.map(c => (
                  <button key={c} type="button" onClick={() => set("theme_color", c)} aria-label={`Pick ${c}`} className={`w-7 h-7 rounded-full border-2 transition ${bot.theme_color === c ? "border-foreground scale-110" : "border-transparent"}`} style={{ backgroundColor: c }} data-testid={`palette-${c.replace('#','')}`}/>
                ))}
              </div>
            </div>
          </div>

          {/* Fallback + disclaimer + footer note */}
          <div><Label>Fallback response (when answer not in docs)</Label><Textarea data-testid="cb-fallback" rows={2} className="mt-1.5" value={bot.fallback_response || ""} onChange={e => set("fallback_response", e.target.value)}/></div>
          <div><Label>Disclaimer</Label><Textarea data-testid="cb-disclaimer" rows={2} className="mt-1.5" value={bot.disclaimer || ""} onChange={e => set("disclaimer", e.target.value)}/></div>
          <div><Label>Footer note (small print)</Label><Input data-testid="branding-footer" className="mt-1.5" value={bot.footer_note || ""} onChange={e => set("footer_note", e.target.value)} placeholder="© Your Business, 2026"/></div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Notification email</Label><Input data-testid="cb-notif" className="mt-1.5" value={bot.notification_email || ""} onChange={e => set("notification_email", e.target.value)}/></div>
            <div><Label>Support phone (shown to visitors)</Label><Input data-testid="branding-phone" className="mt-1.5" value={bot.support_phone || ""} onChange={e => set("support_phone", e.target.value)} placeholder="+1 555 …"/></div>
          </div>

          {/* Step 4 / Part B — Feature flags. Plain checkbox toggles per spec —
              no role/permission system added, no orchestration. The set of
              keys is allow-listed on the backend (sanitize_feature_flags).
              v1.0.17 — the set of toggles SHOWN is now industry-aware so a
              restaurant tenant doesn't see RCM/ProCare-IT toggles and a
              healthcare practice doesn't see catering toggles. */}
          <div className="pt-2 border-t" data-testid="feature-flags-section">
            <Label className="text-sm font-medium">Feature toggles</Label>
            <p className="text-xs text-muted-foreground mt-1 mb-3">
              Enable optional capabilities for your chatbot.
            </p>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {industryFeatureFlags(industry).map(({ id, label }) => {
                const flags = (bot && bot.feature_flags) || {};
                const checked = !!flags[id];
                return (
                  <label
                    key={id}
                    className="flex items-center gap-2.5 px-3 py-2 border rounded-md cursor-pointer hover:bg-accent/30"
                    data-testid={`feature-flag-row-${id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        set("feature_flags", { ...flags, [id]: e.target.checked })
                      }
                      data-testid={`feature-flag-${id}`}
                    />
                    <span className="text-sm">{label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <Button onClick={save} disabled={saving} data-testid="cb-save" className="bg-primary hover:bg-primary/90">{saving ? "Saving..." : "Save branding"}</Button>
        </Card>

        {/* Live preview */}
        <Card className="p-0 overflow-hidden h-fit lg:sticky lg:top-6" data-testid="branding-preview">
          <div className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground border-b">Live preview</div>
          <div className="p-5" style={{ background: `linear-gradient(180deg, ${previewBg}1f 0%, transparent 60%)` }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg grid place-items-center overflow-hidden" style={{ backgroundColor: previewBg }}>
                {logoSrc ? <img src={logoSrc} alt="" className="max-w-full max-h-full object-contain"/> : <Bot className="w-5 h-5 text-white"/>}
              </div>
              <div>
                <div className="font-display font-semibold leading-tight">{bot.name || "Your Assistant"}</div>
                <div className="text-xs text-muted-foreground">Online · ready to help</div>
              </div>
            </div>
            <div className="rounded-lg bg-card p-3 text-sm border max-w-[260px]">{bot.welcome_message || "Hi! How can I help you today?"}</div>
            <div className="mt-3 flex justify-end">
              <div className="rounded-lg p-3 text-sm max-w-[180px]" style={{ backgroundColor: previewBg, color: "white" }}>How are you?</div>
            </div>
            {bot.footer_note && <div className="text-[10px] text-muted-foreground mt-4 text-center">{bot.footer_note}</div>}
          </div>
        </Card>
      </div>

      {/* Iter62B — Settings → Notifications → Conversation Settings */}
      <ConversationSettingsCard />
    </DashboardLayout>
  );
}

// Iter62B — Tenant-level conversation settings UI.
// Maps the existing /api/conversation-settings backend fields to the
// five toggles approved in the product spec:
//   • Auto Send Chat Transcript          → auto_send_chat_transcript     (master gate)
//   • Send Transcript To Customer        → transcript_send_to_customer
//   • Send Transcript To Business        → transcript_send_to_business
//   • Require Customer Email Before Send → transcript_only_if_lead_captured
//   • Enable Transcript Storage          → enable_transcript_storage
// Lives under Branding (Settings → Notifications → Conversation Settings)
// because Branding is the existing tenant-level settings surface — no
// new route added per the "no architecture redesign" constraint.
function ConversationSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [baseline, setBaseline] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/conversation-settings");
        if (cancelled) return;
        setSettings(data.settings);
        setDefaults(data.defaults);
        setBaseline(JSON.stringify(data.settings));
      } catch (err) {
        if (err?.response?.status !== 401) {
          toast.error(err.response?.data?.detail || "Could not load conversation settings.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev?.[key] }));
  };

  const save = async () => {
    if (!settings || saving) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/conversation-settings", settings);
      setSettings(data.settings);
      setBaseline(JSON.stringify(data.settings));
      toast.success("Conversation settings saved");
    } catch (err) {
      if (err?.response?.status !== 401) {
        toast.error(err.response?.data?.detail || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const isDirty = settings && JSON.stringify(settings) !== baseline;

  // Field config — keeps the UI declarative and easy to extend later.
  const rows = [
    { key: "auto_send_chat_transcript",
      label: "Auto Send Chat Transcript",
      help: "Master switch. When OFF, no transcripts ship even if the per-channel toggles below are enabled.",
      testid: "cs-auto-send" },
    { key: "transcript_send_to_customer",
      label: "Send Transcript To Customer",
      help: "Email the chat transcript to the customer when the session closes.",
      testid: "cs-send-customer" },
    { key: "transcript_send_to_business",
      label: "Send Transcript To Business",
      help: "Also email a copy to the business support / owner inbox.",
      testid: "cs-send-business" },
    { key: "transcript_only_if_lead_captured",
      label: "Require Customer Email Before Sending",
      help: "Only deliver transcripts when a customer email was captured during the chat. Recommended ON.",
      testid: "cs-require-email" },
    { key: "enable_transcript_storage",
      label: "Enable Transcript Storage",
      help: "Persist the transcript in audit logs for compliance and history. Recommended ON.",
      testid: "cs-enable-storage" },
  ];

  return (
    <div className="mt-8" data-testid="conversation-settings-section">
      <h2 className="font-display text-2xl font-semibold mb-1">Notifications</h2>
      <p className="text-muted-foreground mb-5 text-sm">
        How and when the AI Assistant follows up with customers after a chat.
      </p>
      <Card className="p-6 space-y-5" data-testid="conversation-settings-card">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-base">Conversation Settings</h3>
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              Tenant-level
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Applies to Retail Storefront Chat, Public AI Assistant Chat, and
            future conversation surfaces.
          </p>
        </div>

        {loading ? (
          <div className="text-sm text-muted-foreground" data-testid="conversation-settings-loading">
            Loading current settings…
          </div>
        ) : settings ? (
          <div className="space-y-3">
            {rows.map((r) => (
              <label
                key={r.key}
                className="flex items-start gap-3 p-3 border rounded-md cursor-pointer hover:bg-accent/30"
                data-testid={`${r.testid}-row`}
              >
                <input
                  type="checkbox"
                  checked={!!settings[r.key]}
                  onChange={() => toggle(r.key)}
                  className="mt-0.5 h-4 w-4"
                  data-testid={r.testid}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{r.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{r.help}</div>
                  {defaults && defaults[r.key] !== undefined && (
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                      Default: {defaults[r.key] ? "ON" : "OFF"}
                    </div>
                  )}
                </div>
              </label>
            ))}
            <div className="pt-2 flex items-center gap-3">
              <Button
                onClick={save}
                disabled={!isDirty || saving}
                data-testid="conversation-settings-save"
                className="bg-primary hover:bg-primary/90"
              >
                {saving ? "Saving…" : "Save conversation settings"}
              </Button>
              {isDirty ? (
                <span className="text-xs text-amber-700" data-testid="conversation-settings-dirty">
                  Unsaved changes
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">All changes saved</span>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-amber-700" data-testid="conversation-settings-error">
            Couldn&apos;t load settings. Refresh the page or try again later.
          </div>
        )}
      </Card>
    </div>
  );
}


// One row in the Documents table. Website docs are expandable to reveal the
// list of pages we actually crawled (titles + URLs + per-page chunk counts).
function DocumentRow({ doc, onDelete, onEdit, onPreview, onDownload, onRename, onAssignmentChange, assignmentMapping, assistantsList }) {
  const [expanded, setExpanded] = useState(false);
  const [pages, setPages] = useState(null);
  const [loadingPages, setLoadingPages] = useState(false);
  const d = doc;
  const isWebsite = d.file_type === "website" || d.source_type === "website" || d.source_type === "website_edited";
  const isManual = d.file_type === "manual" || d.source_type === "manual";
  const isImage = d.file_type === "image" || d.source_type === "image";
  // Content-editing modal is only for text-based docs. Images and uploaded
  // PDFs/CSVs/XLSX keep their structured/binary content as-is (re-upload
  // to replace). The new Actions column always lets the user rename,
  // preview, and download regardless of type.
  const canEditContent = (isWebsite || isManual) && !isImage;
  const hasFile = isImage || Boolean(d.storage_path);
  const statusBadge = (() => {
    const s = d.status;
    if (s === "processing") return <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">processing</Badge>;
    if (s === "failed")     return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">failed</Badge>;
    if (d.source_type === "website_edited") return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">edited</Badge>;
    if (isImage) return <Badge variant="outline" className="bg-pink-50 text-pink-700 border-pink-200">image</Badge>;
    if (isManual) return <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">manual</Badge>;
    return <Badge variant="default" className="bg-emerald-100 text-emerald-800">completed</Badge>;
  })();
  const toggle = async () => {
    if (!isWebsite) return;
    const next = !expanded;
    setExpanded(next);
    if (next && pages === null) {
      setLoadingPages(true);
      try {
        const { data } = await api.get(`/documents/${d.id}/pages`);
        setPages(data.pages || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load pages");
        setPages([]);
      } finally { setLoadingPages(false); }
    }
  };
  return (
    <>
      <TableRow data-testid={`doc-row-${d.id}`}>
        <TableCell className="align-top pt-3">
          {isWebsite ? (
            <button onClick={toggle} className="text-muted-foreground hover:text-foreground" data-testid={`doc-expand-${d.id}`} aria-label={expanded ? "Collapse" : "Expand"}>
              {expanded ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
            </button>
          ) : null}
        </TableCell>
        <TableCell>
          <div className="flex items-start gap-2">
            {isImage ? <FileText className="w-4 h-4 mt-0.5 text-pink-600"/> : isManual ? <Pencil className="w-4 h-4 mt-0.5"/> : isWebsite ? <Globe className="w-4 h-4 mt-0.5"/> : d.file_type === "pdf" ? <FileText className="w-4 h-4 mt-0.5"/> : <FileSpreadsheet className="w-4 h-4 mt-0.5"/>}
            <div className="min-w-0">
              <div className="font-medium truncate max-w-xs" title={d.title || d.filename}>{d.title || d.filename}</div>
              <button
                type="button"
                onClick={() => { try { navigator.clipboard?.writeText(d.id); toast.success("Doc ID copied"); } catch (_) {} }}
                className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground hover:text-emerald-700"
                data-testid={`doc-id-chip-${d.id}`}
                title="Click to copy Doc ID"
              >
                <span className="bg-muted/60 px-1.5 py-0.5 rounded">ID: {String(d.id).slice(0, 8)}…</span>
                <Copy className="w-3 h-3 opacity-60" />
              </button>
              {d.doc_code && <div className="text-[10px] font-mono text-muted-foreground" data-testid={`doc-code-${d.id}`}>{d.doc_code}</div>}
              {d.normalized_domain && <div className="text-xs text-muted-foreground" data-testid={`doc-domain-${d.id}`}>Domain: <span className="font-mono">{d.normalized_domain}</span></div>}
              {d.source_url && <div className="text-xs text-muted-foreground truncate max-w-md" title={d.source_url}>Source: {d.source_url}</div>}
              {d.last_error && <div className="text-xs text-red-700" data-testid={`doc-error-${d.id}`}>Error: {d.last_error}</div>}
            </div>
          </div>
        </TableCell>
        <TableCell><Badge variant="secondary">{isImage ? "image" : d.source_type === "manual" ? "manual" : d.source_type === "website_edited" ? "website" : d.file_type}</Badge></TableCell>
        <TableCell>{statusBadge}</TableCell>
        <TableCell>{isWebsite ? (d.pages_crawled ?? "—") : "—"}</TableCell>
        <TableCell>{d.chunk_count}</TableCell>
        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{new Date(d.updated_at || d.uploaded_at).toLocaleDateString()}</TableCell>
        <TableCell>
          <div className="flex gap-1 justify-end flex-wrap items-center">
            <ResourceAssignChips
              doc={d}
              onChange={onAssignmentChange}
              initialMapping={assignmentMapping}
              initialAssistants={assistantsList}
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => (hasFile ? onDownload(d) : onPreview(d))}
              data-testid={`doc-download-${d.id}`}
              title={hasFile ? "Download" : "View / Download"}
            >
              <Download className="w-4 h-4"/>
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onRename(d)} data-testid={`doc-rename-${d.id}`} title="Edit filename">
              <Type className="w-4 h-4"/>
            </Button>
            {canEditContent && (
              <Button size="sm" variant="ghost" onClick={() => onEdit(d)} data-testid={`doc-edit-${d.id}`} title="Edit content">
                <Pencil className="w-4 h-4"/>
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => onDelete(d.id)} data-testid={`doc-del-${d.id}`} title="Delete">
              <Trash2 className="w-4 h-4"/>
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow data-testid={`doc-pages-${d.id}`}>
          <TableCell colSpan={8} className="bg-muted/30 px-6 py-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">Imported pages</div>
            {loadingPages ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin"/> Loading…</div>
            ) : pages && pages.length > 0 ? (
              <ul className="space-y-1.5 text-sm">
                {pages.map(p => (
                  <li key={p.source_url} className="flex items-start gap-2">
                    <span className="text-muted-foreground">·</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{p.title || p.source_url}</div>
                      <a href={p.source_url} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline break-all">{p.source_url}</a>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{p.chunk_count} chunk{p.chunk_count === 1 ? "" : "s"}</Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-muted-foreground">No page metadata available.</div>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}


// ============================================================================
// Proposed Q&A v1 — tenant-scoped review / approve / reject / edit workflow.
// Rendered below the Resources table. No RAG ingestion here.
// ============================================================================
function ProposedQAStatusBadge({ status, isOverridden }) {
  const map = {
    Proposed: "bg-slate-100 text-slate-700 border-slate-200",
    Approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Edited:   "bg-amber-50 text-amber-800 border-amber-200",
    Rejected: "bg-rose-50 text-rose-700 border-rose-200",
    "Conflict Detected": "bg-orange-100 text-orange-800 border-orange-300",
  };
  if (isOverridden) {
    return (
      <Badge variant="outline" className="bg-violet-100 text-violet-800 border-violet-300">
        Overridden
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className={map[status] || ""}>
      {status || "—"}
    </Badge>
  );
}

function ProposedQAOverrideDialog({ row, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  if (!row) return null;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/proposed-qa/${row.id}/override`, { reason: reason.trim() || undefined });
      toast.success(`Conflict overridden · ${data.qa_code}`);
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Override failed.");
    } finally { setSaving(false); }
  };
  const cs = row.conflict_state || {};
  return (
    <div
      data-testid="proposed-qa-override-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose?.(); }}
    >
      <div className="bg-background w-full max-w-xl rounded-xl shadow-2xl border overflow-hidden">
        <div className="px-5 py-3.5 border-b bg-card">
          <div className="font-semibold text-sm flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-orange-600" /> Override conflict?
          </div>
          <div className="text-[11px] font-mono text-muted-foreground mt-0.5">{row.qa_code}</div>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div className="rounded-md border border-orange-200 bg-orange-50 p-3 text-orange-900 text-xs">
            <div className="font-semibold mb-1">Conflict detected ({cs.confidence || "Medium"} confidence)</div>
            <div className="text-orange-800 whitespace-pre-wrap leading-snug">{cs.reason || "(no reason recorded)"}</div>
            <div className="mt-2 pt-2 border-t border-orange-200 text-[11px]">
              <span className="font-semibold">Conflicts with:</span> {cs.source_title || "—"}
              {cs.source_doc_code && <span className="font-mono ml-1">({cs.source_doc_code})</span>}
            </div>
            <div className="mt-1 text-[11px] italic">"{cs.statement || "—"}"</div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Override reason (optional)</Label>
            <textarea
              data-testid="proposed-qa-override-reason"
              className="mt-1 w-full min-h-[80px] rounded-md border border-input bg-background p-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="e.g. Approved by Ops manager — promotion in effect"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={saving}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            Overriding will mark the row as <strong>Overridden</strong> and ingest it into the chatbot's knowledge base. The audit trail (who + when + reason) is preserved.
          </p>
        </div>
        <div className="px-5 py-3 border-t bg-muted/30 flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose} disabled={saving} data-testid="proposed-qa-override-cancel">Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="proposed-qa-override-confirm" className="bg-orange-600 hover:bg-orange-700 text-white">
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Overriding…</> : "Override & approve"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ProposedQAEditDialog({ row, onClose, onSaved }) {
  const [answer, setAnswer] = useState(row?.answer || "");
  const [saving, setSaving] = useState(false);
  if (!row) return null;
  const save = async () => {
    if (!answer.trim()) { toast.error("Answer cannot be empty."); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/proposed-qa/${row.id}`, { answer: answer.trim() });
      toast.success(`Answer updated · ${data.qa_code}`);
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save edit.");
    } finally { setSaving(false); }
  };
  return (
    <div
      data-testid="proposed-qa-edit-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose?.(); }}
    >
      <div className="bg-background w-full max-w-2xl rounded-xl shadow-2xl border overflow-hidden">
        <div className="px-5 py-3.5 border-b flex items-center justify-between gap-3 bg-card">
          <div className="min-w-0">
            <div className="font-semibold text-sm">Edit answer</div>
            <div className="text-[11px] font-mono text-muted-foreground">{row.qa_code}</div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} disabled={saving} data-testid="proposed-qa-edit-close">
            <X className="w-4 h-4"/>
          </Button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label className="text-xs text-muted-foreground">Question</Label>
            <div className="text-sm bg-muted/40 rounded-md p-3 mt-1">{row.question}</div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Answer</Label>
            <textarea
              data-testid="proposed-qa-edit-answer"
              className="mt-1 w-full min-h-[160px] rounded-md border border-input bg-background p-3 text-sm font-sans focus:outline-none focus:ring-1 focus:ring-ring"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              disabled={saving}
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Saving will mark this row as <strong>Edited</strong>, run a contradiction check against your tenant's existing knowledge, and — if clean — ingest the new answer into the chatbot's knowledge base.
            </p>
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-muted/30 flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose} disabled={saving} data-testid="proposed-qa-edit-cancel">Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving || !answer.trim()} data-testid="proposed-qa-edit-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Saving…</> : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ProposedQASection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  // v1.0.11 — Regenerate Questions dialog state machine:
  //   "augment"     → "Add Q&A from new documents" (default, safe)
  //   "regenerate"  → "Replace Proposed/Rejected with fresh Q&A" (preserves Approved/Edited)
  //   null          → dialog closed
  const [regenChoice, setRegenChoice] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [overridingRow, setOverridingRow] = useState(null);
  const [expandedId, setExpandedId] = useState(null);  // conflict-row drawer
  const [bulkConfirm, setBulkConfirm] = useState(null);  // null | "approve_all" | "revert_all"
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/proposed-qa");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      // Bug fix (Proposed Q&A renders empty after generate):
      // Previously we silently swallowed 401s here, which left the table
      // in its initial-empty state when an impersonation / JWT happened to
      // expire during a long generate call. Surface the failure with an
      // actionable toast so the user can reload instead of seeing a
      // misleading "no proposed questions yet" empty state. The api.js
      // response interceptor still owns the impersonation auto-redirect.
      const status = e?.response?.status;
      const isImpersonationRedirect = status === 401 && !!localStorage.getItem("sa_token_backup");
      if (!isImpersonationRedirect) {
        toast.error(e?.response?.data?.detail || "Couldn't load Proposed Q&A — please reload the page.");
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // v1.0.11 — listen for new resources. If the tenant already has Q&A,
  // silently kick an "augment" generate so new documents grow the pool
  // without disturbing Approved rows. Debounced via the same 800ms idle
  // window the provisioning panel uses, so multiple rapid adds collapse
  // into one generate call.
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const generateAugmentRef = useRef(null);
  useEffect(() => {
    let t = null;
    const onChange = (e) => {
      // Skip when the change is a delete or when we don't have any rows yet
      // (first generate is explicit). Also skip when a generate is in-flight.
      if (e?.detail?.source === "delete") return;
      if (rowsRef.current.length === 0) return;
      if (t) clearTimeout(t);
      t = setTimeout(() => { generateAugmentRef.current?.({ silent: true }); }, 800);
    };
    window.addEventListener("rudras:resources-changed", onChange);
    return () => { window.removeEventListener("rudras:resources-changed", onChange); if (t) clearTimeout(t); };
  }, []);

  // v1.0.20 — poll a background Q&A generation job. Returns the final
  // `result` payload (same shape the sync route returned). Throws on
  // failure so callers' existing catch blocks still surface toasts.
  const pollProposedQaJob = async (jobId, { timeoutMs = 180000, intervalMs = 1500 } = {}) => {
    const startedAt = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (Date.now() - startedAt > timeoutMs) {
        throw new Error("Generation is still running — please refresh the page in a moment.");
      }
      // eslint-disable-next-line no-await-in-loop
      const { data: job } = await api.get(`/proposed-qa/generate/${jobId}`);
      if (job?.status === "completed") return job.result || {};
      if (job?.status === "failed") {
        const detail = job?.error?.detail || "Generation failed. Please try again.";
        const err = new Error(detail);
        err.response = { status: job?.error?.status_code || 500, data: { detail } };
        throw err;
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise(r => setTimeout(r, intervalMs));
    }
  };

  const generateAugment = async (opts = {}) => {
    setGenerating(true);
    try {
      // v1.0.20 — async background job to avoid CDN 524 timeouts.
      const { data: started } = await api.post("/proposed-qa/generate", { mode: "augment", async_mode: true });
      // Augment with no new docs returns immediately (no job_id) with the
      // "All documents already have Q&A coverage." message.
      const data = started?.job_id ? await pollProposedQaJob(started.job_id) : started;
      if (Array.isArray(data?.rows) && data.rows.length > 0) setRows(data.rows);
      else await load();
      if (!opts.silent) {
        const added = data?.active ?? data?.generated ?? 0;
        toast.success(
          added > 0
            ? `Added ${added} new Q&A from your latest documents.`
            : (data?.message || "All documents already have Q&A coverage.")
        );
      }
    } catch (e) {
      if (!opts.silent) {
        toast.error(e.response?.data?.detail || e.message || "Couldn't add Q&A from new documents.");
      }
    } finally {
      setGenerating(false);
      setRegenChoice(null);
    }
  };
  useEffect(() => { generateAugmentRef.current = generateAugment; });

  const generateRegenerate = async () => {
    setGenerating(true);
    try {
      // v1.0.20 — async background job to avoid CDN 524 timeouts.
      const { data: started } = await api.post("/proposed-qa/generate", { mode: "regenerate", async_mode: true });
      const data = await pollProposedQaJob(started?.job_id);
      if (Array.isArray(data?.rows)) setRows(data.rows);
      else await load();
      toast.success(
        `Regenerated ${data?.active ?? 0} questions · ${data?.preserved_curated ?? 0} approved preserved.`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Regenerate failed. Try again.");
    } finally {
      setGenerating(false);
      setRegenChoice(null);
    }
  };

  const generateInitial = async () => {
    setGenerating(true);
    try {
      // v1.0.20 — async background job to avoid CDN 524 timeouts.
      const { data: started } = await api.post("/proposed-qa/generate", { async_mode: true });
      const data = await pollProposedQaJob(started?.job_id);
      if (Array.isArray(data?.rows)) setRows(data.rows);
      else await load();
      toast.success(`Generated ${data?.active ?? 0} questions · ${data?.reserve ?? 0} held in reserve.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Generation failed. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  const onGenerateClick = () => {
    if (rows.length === 0) { generateInitial(); return; }
    setRegenChoice("augment");  // open dialog with augment preselected
  };

  const approve = async (row) => {
    try {
      const { data } = await api.post(`/proposed-qa/${row.id}/approve`);
      setRows((rs) => rs.map((r) => (r.id === row.id ? data : r)));
      toast.success(`Approved · ${data.qa_code}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed.");
    }
  };

  // v1.0.11 — Approved → Proposed toggle. Reversible per stakeholder spec.
  const revert = async (row) => {
    try {
      const { data } = await api.post(`/proposed-qa/${row.id}/revert`);
      setRows((rs) => rs.map((r) => (r.id === row.id ? data : r)));
      toast.success("Reverted to Proposed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revert failed.");
    }
  };

  // v1.0.11 — Row status pill click → toggle Proposed ↔ Approved.
  const toggleStatus = (row) => {
    if (row.status === "Approved" || row.status === "Edited") {
      revert(row);
    } else if (row.status === "Proposed") {
      approve(row);
    } else if (row.status === "Conflict Detected") {
      setOverridingRow(row);
    }
  };

  // v1.0.11 — bulk approve / revert across the active rows.
  const runBulk = async (action) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/proposed-qa/bulk", { action });
      if (Array.isArray(data?.rows)) setRows(data.rows);
      else await load();
      toast.success(
        action === "approve_all"
          ? `Marked ${data?.updated_count ?? 0} Q&A as Approved.`
          : `Reverted ${data?.updated_count ?? 0} Q&A back to Proposed.`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk update failed.");
    } finally {
      setBulkBusy(false);
      setBulkConfirm(null);
    }
  };

  const reject = async (row) => {
    try {
      const { data } = await api.post(`/proposed-qa/${row.id}/reject`);
      setRows((rs) => {
        const filtered = rs.filter((r) => r.id !== row.id);
        if (data.promoted) {
          // Insert the promoted item at the rejected row's old position.
          const idx = rs.findIndex((r) => r.id === row.id);
          if (idx >= 0) {
            const next = [...filtered];
            next.splice(idx, 0, data.promoted);
            return next;
          }
          return [...filtered, data.promoted];
        }
        return filtered;
      });
      toast.success(data.promoted ? "Rejected · replaced from reserve" : "Rejected");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed.");
    }
  };

  const hasRows = rows.length > 0;

  return (
    <Card className="p-6 mt-6" data-testid="proposed-qa-section">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-accent/15 grid place-items-center">
          <Sparkles className="w-5 h-5 text-accent" />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-xl font-semibold">Proposed Q&amp;A</h2>
          <p className="text-sm text-muted-foreground">
            Review and approve suggested prospect questions and answers to strengthen your Sales Assistant knowledge base.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasRows && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setBulkConfirm("approve_all")}
                disabled={bulkBusy || generating}
                data-testid="proposed-qa-bulk-approve-btn"
                className="text-xs"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1 text-emerald-700"/>Mark all as Approved
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setBulkConfirm("revert_all")}
                disabled={bulkBusy || generating}
                data-testid="proposed-qa-bulk-revert-btn"
                className="text-xs"
              >
                <X className="w-3.5 h-3.5 mr-1"/>Revert all to Proposed
              </Button>
            </>
          )}
          <Button
            size="sm"
            onClick={onGenerateClick}
            disabled={generating || loading}
            data-testid="proposed-qa-generate-btn"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Generating…</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-1.5" /> {hasRows ? "Regenerate Questions" : "Generate Questions"}</>
            )}
          </Button>
          {hasRows && (
            <Button
              size="sm"
              variant="ghost"
              onClick={load}
              disabled={loading}
              data-testid="proposed-qa-refresh"
              aria-label="Refresh proposed Q&A"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin"/> Loading proposed Q&A…
        </div>
      ) : !hasRows ? (
        <div
          className="rounded-lg border border-dashed bg-muted/30 px-6 py-10 text-center"
          data-testid="proposed-qa-empty-state"
        >
          <div className="mx-auto w-11 h-11 rounded-full bg-accent/10 grid place-items-center mb-3">
            <FileText className="w-5 h-5 text-accent/80" />
          </div>
          <div className="font-medium text-foreground mb-1">No proposed questions yet</div>
          <div className="text-sm text-muted-foreground max-w-md mx-auto">
            Click <strong>Generate Questions</strong> and we'll draft prospect Q&A from your uploaded resources for you to review.
          </div>
        </div>
      ) : (
        <div
          className="max-h-[420px] overflow-y-auto rounded-md border bg-background/60"
          data-testid="proposed-qa-table-scroll"
        >
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10 shadow-[inset_0_-1px_0_0_var(--border)]">
              <TableRow>
                <TableHead className="w-[34%]">Question</TableHead>
                <TableHead className="w-[34%]">Proposed Answer</TableHead>
                <TableHead className="w-[14%]">Source</TableHead>
                <TableHead className="w-[8%]">Status</TableHead>
                <TableHead className="text-right w-[10%]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                <TableRow data-testid={`proposed-qa-row-${r.id}`} className={r.status === "Conflict Detected" ? "bg-orange-50/40" : ""}>
                  <TableCell className="align-top">
                    <div className="text-sm font-medium leading-snug flex items-start gap-1.5">
                      {r.status === "Conflict Detected" && (
                        <ShieldAlert
                          className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0 cursor-pointer"
                          data-testid={`proposed-qa-conflict-icon-${r.id}`}
                          onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                          title="Conflict detected — click for details"
                        />
                      )}
                      <span>{r.question}</span>
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground mt-1">{r.qa_code}</div>
                  </TableCell>
                  <TableCell className="align-top text-sm text-muted-foreground whitespace-pre-wrap leading-snug">
                    {r.answer}
                  </TableCell>
                  <TableCell className="align-top">
                    {r.source_confirmed ? (
                      <div>
                        <div className="text-xs font-medium truncate" title={r.source_title}>{r.source_title || "—"}</div>
                        {r.source_doc_code && (
                          <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{r.source_doc_code}</div>
                        )}
                      </div>
                    ) : (
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px] font-normal" title="No source document confirmed this answer">
                        AI suggested
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="align-top">
                    <button
                      type="button"
                      onClick={() => toggleStatus(r)}
                      className="cursor-pointer transition-opacity hover:opacity-80 disabled:cursor-default"
                      data-testid={`proposed-qa-status-toggle-${r.id}`}
                      title={r.status === "Approved" || r.status === "Edited"
                        ? "Click to revert to Proposed"
                        : r.status === "Proposed" ? "Click to approve" : ""}
                    >
                      <ProposedQAStatusBadge status={r.status} isOverridden={Boolean(r.override)} />
                    </button>
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex items-center gap-1 justify-end">
                      {r.status === "Conflict Detected" ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Override conflict & approve"
                          onClick={() => setOverridingRow(r)}
                          data-testid={`proposed-qa-override-${r.id}`}
                          className="h-7 w-7 p-0"
                        >
                          <ShieldAlert className="w-4 h-4 text-orange-700"/>
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Approve"
                          onClick={() => approve(r)}
                          disabled={r.status === "Approved"}
                          data-testid={`proposed-qa-approve-${r.id}`}
                          className="h-7 w-7 p-0"
                        >
                          <CheckCircle2 className="w-4 h-4 text-emerald-700"/>
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Edit answer"
                        onClick={() => setEditingRow(r)}
                        data-testid={`proposed-qa-edit-${r.id}`}
                        className="h-7 w-7 p-0"
                      >
                        <Pencil className="w-4 h-4 text-amber-700"/>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Reject"
                        onClick={() => reject(r)}
                        data-testid={`proposed-qa-reject-${r.id}`}
                        className="h-7 w-7 p-0"
                      >
                        <XCircle className="w-4 h-4 text-rose-600"/>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {(r.status === "Conflict Detected" && r.conflict_state && expandedId === r.id) && (
                  <TableRow data-testid={`proposed-qa-conflict-drawer-${r.id}`} className="bg-orange-50/60">
                    <TableCell colSpan={5} className="py-3">
                      <div className="rounded-md border border-orange-200 bg-orange-50 p-3 text-xs">
                        <div className="flex items-center gap-2 mb-2">
                          <ShieldAlert className="w-4 h-4 text-orange-600"/>
                          <span className="font-semibold text-orange-900">Conflict detected</span>
                          <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300 ml-auto text-[10px]">
                            {r.conflict_state.confidence || "Medium"} confidence
                          </Badge>
                        </div>
                        <div className="text-orange-900 leading-snug mb-2">
                          {r.conflict_state.reason || "(no reason recorded)"}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-orange-200">
                          <div>
                            <div className="font-semibold mb-1 text-orange-900">Your answer</div>
                            <div className="rounded bg-background/70 border border-orange-100 p-2 leading-snug">{r.answer}</div>
                          </div>
                          <div>
                            <div className="font-semibold mb-1 text-orange-900">Conflicts with</div>
                            <div className="rounded bg-background/70 border border-orange-100 p-2 leading-snug">
                              <div className="italic">"{r.conflict_state.statement || "—"}"</div>
                              <div className="mt-1.5 text-[10px] text-orange-800">
                                {r.conflict_state.source_title || "Unknown source"}
                                {r.conflict_state.source_doc_code && <span className="font-mono ml-1">({r.conflict_state.source_doc_code})</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => setEditingRow(r)} data-testid={`proposed-qa-conflict-resolve-${r.id}`}>
                            <Pencil className="w-3.5 h-3.5 mr-1.5"/> Resolve by editing
                          </Button>
                          <Button size="sm" onClick={() => setOverridingRow(r)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid={`proposed-qa-conflict-override-${r.id}`}>
                            <ShieldAlert className="w-3.5 h-3.5 mr-1.5"/> Override & approve
                          </Button>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground mt-3">
        Approved, Edited, and Overridden answers are <strong>ingested into your chatbot's knowledge base</strong> and prioritized over generic resource text.
      </p>

      {/* Regenerate confirmation */}
      {/* v1.0.11 — Regenerate Questions dialog: 2 modes (augment vs full regenerate) */}
      <AlertDialog open={regenChoice !== null} onOpenChange={(open) => { if (!open) setRegenChoice(null); }}>
        <AlertDialogContent data-testid="proposed-qa-regenerate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate questions</AlertDialogTitle>
            <AlertDialogDescription>
              Choose how to refresh your Q&amp;A list. Approved and Edited rows are always preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 my-2">
            <label className="flex items-start gap-3 p-3 rounded-md border border-emerald-200 bg-emerald-50/40 cursor-pointer hover:bg-emerald-50">
              <input
                type="radio"
                name="regen-mode"
                value="augment"
                checked={regenChoice === "augment"}
                onChange={() => setRegenChoice("augment")}
                className="mt-1"
                data-testid="proposed-qa-regen-mode-augment"
              />
              <div>
                <div className="font-medium text-sm">Add Q&amp;A from new documents <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded ml-1">recommended</span></div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Only generates Q&amp;A for documents you've added since the last run. Existing rows stay untouched.
                </div>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-md border border-rose-200 bg-rose-50/40 cursor-pointer hover:bg-rose-50">
              <input
                type="radio"
                name="regen-mode"
                value="regenerate"
                checked={regenChoice === "regenerate"}
                onChange={() => setRegenChoice("regenerate")}
                className="mt-1"
                data-testid="proposed-qa-regen-mode-regenerate"
              />
              <div>
                <div className="font-medium text-sm">Full regenerate <span className="text-[10px] font-mono uppercase tracking-wider text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded ml-1">destructive</span></div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Replaces all <strong>Proposed</strong> and <strong>Rejected</strong> rows with a fresh draft. Approved and Edited rows are kept.
                </div>
              </div>
            </label>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="proposed-qa-regenerate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="proposed-qa-regenerate-confirm"
              onClick={(e) => {
                e.preventDefault();
                if (regenChoice === "regenerate") generateRegenerate();
                else generateAugment();
              }}
            >
              {regenChoice === "regenerate" ? "Regenerate everything" : "Add new Q&A"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* v1.0.11 — Bulk approve / revert confirmation */}
      <AlertDialog open={bulkConfirm !== null} onOpenChange={(open) => { if (!open) setBulkConfirm(null); }}>
        <AlertDialogContent data-testid="proposed-qa-bulk-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkConfirm === "approve_all" ? "Mark all Q&A as Approved?" : "Revert all Q&A to Proposed?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will change the status for all Q&amp;A items. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="proposed-qa-bulk-cancel" disabled={bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="proposed-qa-bulk-confirm"
              disabled={bulkBusy}
              onClick={(e) => { e.preventDefault(); runBulk(bulkConfirm); }}
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : (bulkConfirm === "approve_all" ? "Approve all" : "Revert all")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {editingRow && (
        <ProposedQAEditDialog
          row={editingRow}
          onClose={() => setEditingRow(null)}
          onSaved={(updated) => {
            setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
            // If the edit produced a conflict, auto-open its drawer.
            if (updated?.status === "Conflict Detected") setExpandedId(updated.id);
          }}
        />
      )}

      {overridingRow && (
        <ProposedQAOverrideDialog
          row={overridingRow}
          onClose={() => setOverridingRow(null)}
          onSaved={(updated) => {
            setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
            setExpandedId(null);
          }}
        />
      )}
    </Card>
  );
}



export function Resources() {
  const [docs, setDocs] = useState([]);
  const [storage, setStorage] = useState(null); // {used_bytes, quota_bytes, remaining_bytes}
  const [uploading, setUploading] = useState(false);
  const [siteUrl, setSiteUrl] = useState("");
  // Stage machine: 'ready' | 'validating' | 'awaiting_confirm' | 'crawling' | 'extracting' | 'chunking' | 'completed' | 'failed'
  const [stage, setStage] = useState("ready");
  const [stageError, setStageError] = useState("");
  const [confirmInfo, setConfirmInfo] = useState(null); // { cleaned_url, normalized_domain, existing }
  // Document editor modal state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("manual");  // "manual" | "edit"
  const [editingDocId, setEditingDocId] = useState("");
  const [editingTitle, setEditingTitle] = useState("");
  // Preview + rename state
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [renameDoc, setRenameDoc] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  // v1.0.10 — persisted assignment map. Without this, ResourceAssignChips
  // only knew its mapping when the popover OPENED, so after a page reload
  // every row's chip showed "+ Assign" even when a persisted backend mapping
  // existed. The user assumed the assignment was lost. We now fetch the full
  // mapping graph ONCE per page mount and pass each doc's slice as a prop.
  // Shape: { assistants: [...], byDoc: { [docId]: { global: bool, scoped: Set<assistant_id> } } }
  const [assignments, setAssignments] = useState({ assistants: [], byDoc: {} });
  const loadAssignments = useCallback(async () => {
    try {
      const [assistantsResp, globalResp] = await Promise.all([
        api.get("/assistants", { params: { include_archived: false } }),
        api.get("/assistants/resources/global"),
      ]);
      const list = assistantsResp.data?.assistants || [];
      const byDoc = {};
      // Seed global mappings first.
      for (const m of (globalResp.data?.resources || [])) {
        if (!m.resource_id) continue;
        byDoc[m.resource_id] = byDoc[m.resource_id] || { global: false, scoped: new Set() };
        byDoc[m.resource_id].global = true;
      }
      // Then scoped per assistant.
      const perAsst = await Promise.all(list.map(a =>
        api.get(`/assistants/${a.id}/resources`).then(r => ({ aid: a.id, rows: r.data?.resources || [] })).catch(() => ({ aid: a.id, rows: [] }))
      ));
      for (const { aid, rows } of perAsst) {
        for (const m of rows) {
          if (!m.resource_id || m.is_global) continue;
          byDoc[m.resource_id] = byDoc[m.resource_id] || { global: false, scoped: new Set() };
          byDoc[m.resource_id].scoped.add(aid);
        }
      }
      setAssignments({ assistants: list, byDoc });
    } catch (err) {
      // Console-only — UI gracefully degrades (chip will say "+ Assign" but
      // the popover open path still reads live state).
      // eslint-disable-next-line no-console
      console.warn("[resource-assignments] failed to preload mappings", err?.response?.status, err?.message);
    }
  }, []);
  // Deep-link auto-focus support — when the Provisioning Panel CTA sends the
  // user here with `?focus=website`, scroll into view + focus the website
  // import input so the next action is one keystroke away.
  const websiteInputRef = useRef(null);
  const websiteCardRef = useRef(null);
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("focus") === "website") {
      // Defer until the card has rendered.
      setTimeout(() => {
        websiteCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        websiteInputRef.current?.focus();
      }, 250);
    }
  }, [location.search]);

  const load = () => Promise.all([
    api.get("/documents").then(r => setDocs(r.data)),
    api.get("/documents/storage").then(r => setStorage(r.data)).catch(() => {}),
  ]);
  useEffect(() => {
    load();
    loadAssignments();
    // Pre-fill from saved business profile website
    api.get("/company").then(r => { if (r.data?.company?.website) setSiteUrl(r.data.company.website); });
  }, [loadAssignments]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name?.trim()) { toast.error("Filename is required."); e.target.value = ""; return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/documents/upload", fd);
      toast.success(`Uploaded ${f.name}`);
      // Product-narrative milestone — fires exactly once per tenant on the
      // very first resource added. The technical toast above stays for
      // immediate confirmation; this layered toast frames the moment as a
      // business event, not a system event.
      fireMilestone("first_resource", MILESTONES.FIRST_RESOURCE);
      load();
      window.dispatchEvent(new CustomEvent("rudras:resources-changed", { detail: { source: "upload" } }));
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  // Authenticated preview/download: /documents/{id}/file requires bearer
  // token, so we fetch as a blob and render via Object URL (cannot use a
  // plain <a href>).
  const openPreview = async (d) => {
    setPreviewDoc(d);
    setPreviewBlobUrl("");
    setPreviewText("");
    setPreviewLoading(true);
    try {
      const isImage = d.file_type === "image" || d.source_type === "image";
      const isText = (d.file_type === "manual" || d.source_type === "manual" ||
                      d.source_type === "website" || d.source_type === "website_edited");
      if (isImage) {
        const resp = await api.get(`/documents/${d.id}/file`, { responseType: "blob" });
        setPreviewBlobUrl(URL.createObjectURL(resp.data));
      } else if (isText) {
        const { data } = await api.get(`/documents/${d.id}/text`);
        setPreviewText(data.content || "(empty)");
      } else {
        downloadDoc(d);
        setPreviewDoc(null);
      }
    } catch (err) {
      if (err?.response?.status !== 401) toast.error(err.response?.data?.detail || "Preview failed");
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };
  const closePreview = () => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl("");
    setPreviewText("");
    setPreviewDoc(null);
  };

  const downloadDoc = async (d) => {
    try {
      const resp = await api.get(`/documents/${d.id}/file?download=1`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.title || d.filename || "download";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      if (err?.response?.status === 404) {
        // Non-binary doc — emit a .txt of the rendered text content.
        try {
          const { data } = await api.get(`/documents/${d.id}/text`);
          const blob = new Blob([data.content || ""], { type: "text/plain;charset=utf-8" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url; a.download = `${(d.title || d.filename || "document")}.txt`;
          document.body.appendChild(a); a.click(); a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e2) {
          if (e2?.response?.status !== 401) toast.error(e2.response?.data?.detail || "Download failed");
        }
      } else if (err?.response?.status !== 401) {
        toast.error(err.response?.data?.detail || "Download failed");
      }
    }
  };

  const openRename = (d) => {
    setRenameDoc(d);
    setRenameValue(d.title || d.filename || "");
  };
  const submitRename = async () => {
    const t = renameValue.trim();
    if (!t) { toast.error("Filename is required."); return; }
    setRenaming(true);
    try {
      await api.patch(`/documents/${renameDoc.id}/filename`, { title: t });
      toast.success("Filename updated");
      setRenameDoc(null);
      load();
    } catch (err) {
      if (err?.response?.status !== 401) toast.error(err.response?.data?.detail || "Rename failed");
    } finally {
      setRenaming(false);
    }
  };

  // Iter62D — ASYNC website import with job-id polling (permanent
  // reliability redesign). POST returns BOTH `job_id` (preferred) and
  // `document_id` (legacy). We poll the job_id for stable progress.
  // Failed jobs surface a Retry button which hits the new `/retry`
  // endpoint instead of stacking duplicate failed rows.
  const runImport = async (cleanedUrl, replace) => {
    setStage("crawling");
    setStageError("");
    try {
      const { data: kicked } = await api.post(
        "/documents/import-website",
        { url: cleanedUrl, max_pages: 30, replace },
      );
      // Iter62D — prefer job_id (stable), fall back to document_id for
      // back-compat. The backend status endpoint accepts both.
      const pollId = kicked.job_id || kicked.document_id;
      if (!pollId) {
        throw new Error("Server did not return a poll id.");
      }

      const pollIntervalMs = 2500;
      const maxAttempts = Math.ceil((6 * 60 * 1000) / pollIntervalMs);
      let attempts = 0;
      let final = null;
      // Iter62D — 5-second grace period for 404s. Tolerates an extreme
      // edge case where the job row hasn't replicated yet.
      let consecutive404 = 0;
      const max404Tolerance = Math.ceil((5 * 1000) / pollIntervalMs);
      await new Promise((r) => setTimeout(r, 1200));
      while (attempts < maxAttempts) {
        attempts += 1;
        try {
          const { data: st } = await api.get(
            `/documents/import-website/status/${pollId}`,
          );
          consecutive404 = 0;
          if (st.pages_fetched > 0 && stage === "crawling") {
            setStage("extracting");
          }
          if (st.chunks > 0 && stage !== "completed") {
            setStage("chunking");
          }
          if (st.is_terminal) {
            final = st;
            break;
          }
        } catch (pollErr) {
          const status = pollErr?.response?.status;
          if (status === 404) {
            consecutive404 += 1;
            if (consecutive404 > max404Tolerance) {
              // Out of grace — abort the poll loop with a clear error.
              throw pollErr;
            }
          } else if (status === 401) {
            throw pollErr;
          }
        }
        await new Promise((r) => setTimeout(r, pollIntervalMs));
      }

      if (!final) {
        setStage("ready");
        toast.warning(
          "Import is taking longer than usual. We'll keep crawling in the background — refresh in a minute.",
        );
        load();
        return;
      }

      if (final.status === "failed" || final.status === "low_content") {
        setStage("failed");
        const msg = final.error_message || final.last_error || "Import failed";
        setStageError(msg);
        toast.error(msg, {
          description: "Tap Import Website again to retry, or upload a PDF instead.",
        });
        load();
        return;
      }

      setStage("completed");
      const pages = final.pages_fetched || final.pages_crawled || 0;
      const verb = final.replaced ? "Replaced" : "Imported";
      if (final.partial_success || final.status === "completed_partial") {
        toast.warning(
          `${verb} ${pages} page${pages === 1 ? "" : "s"} from ${final.domain} (limited access — some pages were rate-limited or blocked).`,
          { description: "We've indexed what we could. Tap Import Website again to fill in the rest." },
        );
      } else {
        toast.success(
          `${verb} ${pages} page${pages === 1 ? "" : "s"} from ${final.domain} (${final.chunks} chunk${final.chunks === 1 ? "" : "s"})`,
        );
      }
      fireMilestone("first_resource", MILESTONES.FIRST_RESOURCE);
      fireMilestone("website_imported", MILESTONES.WEBSITE_IMPORTED);
      load();
      window.dispatchEvent(
        new CustomEvent("rudras:resources-changed", { detail: { source: "website" } }),
      );
      setTimeout(() => setStage("ready"), 2500);
    } catch (err) {
      setStage("failed");
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || err?.message || "Import failed");
      setStageError(msg);
      if (detail && typeof detail === "object" && detail.retryable) {
        toast.error(msg, { description: "Tap Import Website again to retry." });
      } else {
        toast.error(msg);
      }
    }
  };

  // Step 1: validate + check for existing same-domain import.
  const onImport = async () => {
    // Auto-prepend https:// for bare/`www.` inputs so the form Just Works™.
    const u = normalizeWebsiteUrl(siteUrl);
    if (!u) { toast.error("Enter a website URL"); return; }
    if (u !== siteUrl) setSiteUrl(u);
    setStage("validating");
    setStageError("");
    try {
      const { data } = await api.post("/documents/import-website/check", { url: u });
      if (data.existing) {
        // Ask the customer to confirm replacement before doing any work.
        setConfirmInfo(data);
        setStage("awaiting_confirm");
      } else {
        await runImport(data.cleaned_url, false);
      }
    } catch (err) {
      setStage("failed");
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "URL is invalid");
      setStageError(msg);
      toast.error(msg);
    }
  };

  const onConfirmReplace = async () => {
    const info = confirmInfo;
    setConfirmInfo(null);
    if (info) await runImport(info.cleaned_url, true);
  };

  const onCancelReplace = () => {
    setConfirmInfo(null);
    setStage("ready");
  };

  const del = async (id) => {
    await api.delete(`/documents/${id}`);
    toast.success("Deleted");
    load();
    window.dispatchEvent(new CustomEvent("rudras:resources-changed", { detail: { source: "delete" } }));
  };

  // UI stage label / icon
  const STAGE_META = {
    ready:            { label: "Ready",                  cls: "" },
    validating:       { label: "Validating URL…",        cls: "text-muted-foreground" },
    awaiting_confirm: { label: "Awaiting your confirmation", cls: "text-amber-700" },
    crawling:         { label: "Crawling pages…",        cls: "text-muted-foreground" },
    extracting:       { label: "Extracting content…",    cls: "text-muted-foreground" },
    chunking:         { label: "Indexing for AI…",       cls: "text-muted-foreground" },
    completed:        { label: "Completed",              cls: "text-emerald-700" },
    failed:           { label: "Failed",                 cls: "text-red-700" },
  };
  const busy = ["validating", "crawling", "extracting", "chunking"].includes(stage);

  return (
    <DashboardLayout>
      <h1 className="font-display text-4xl font-semibold mb-2" data-testid="resources-page-title">Resources & AI Assistant</h1>
      <p className="text-muted-foreground mb-8">Manage your AI Assistant alongside the knowledge it learns from. PDFs, spreadsheets, JPEGs, or your existing website. Don't upload patient, financial, or confidential employee data.</p>

      {/* AI Assistant control surface — primary assistant identity, public link, QR, active toggle */}
      <AssistantSection />

      {/* Website import */}
      <Card ref={websiteCardRef} className="p-6 mb-6" data-testid="website-import-card">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-accent/15 grid place-items-center"><Globe className="w-5 h-5 text-accent"/></div>
          <div className="flex-1">
            <h2 className="font-display text-xl font-semibold">Import from your website</h2>
            <p className="text-sm text-muted-foreground">We'll crawl up to 30 same-domain pages and turn them into chatbot knowledge — no need to re-upload PDFs.</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            ref={websiteInputRef}
            placeholder="https://yourbusiness.com"
            value={siteUrl}
            onChange={(e) => setSiteUrl(e.target.value)}
            disabled={busy}
            data-testid="import-site-url"
            className="flex-1"
          />
          <Button onClick={onImport} disabled={busy || !siteUrl.trim()} data-testid="import-site-btn" className="bg-primary hover:bg-primary/90">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>{STAGE_META[stage].label}</> : "Import Website"}
          </Button>
        </div>

        {/* Stage indicator */}
        <div className="mt-3 flex items-center gap-2 text-xs" data-testid="import-stage">
          <span className="text-muted-foreground">Status:</span>
          <span className={STAGE_META[stage].cls + " font-medium"} data-testid={`import-stage-${stage}`}>{STAGE_META[stage].label}</span>
          {stage === "failed" && stageError && <span className="text-red-700" data-testid="import-stage-error">— {stageError}</span>}
          {stage === "completed" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/>}
          {stage === "failed" && <XCircle className="w-3.5 h-3.5 text-red-600"/>}
        </div>
        <p className="text-xs text-muted-foreground mt-2">Same-domain pages only. Skips images, PDFs, JS. Public URLs only — private/internal addresses are blocked. Typical run: 10-60s.</p>
      </Card>

      {/* Replace-confirmation dialog */}
      <AlertDialog open={Boolean(confirmInfo)} onOpenChange={(open) => { if (!open) onCancelReplace(); }}>
        <AlertDialogContent data-testid="replace-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-600"/>Replace existing import?</AlertDialogTitle>
            <AlertDialogDescription>
              You already have an import for <strong className="font-mono">{confirmInfo?.normalized_domain}</strong>
              {confirmInfo?.existing?.pages_crawled ? ` (${confirmInfo.existing.pages_crawled} page${confirmInfo.existing.pages_crawled === 1 ? "" : "s"})` : ""}.
              <br/><br/>
              <strong>Re-importing will replace the old website resource and all related AI chunks.</strong> Your PDFs and spreadsheets are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="replace-cancel" onClick={onCancelReplace}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="replace-confirm" onClick={onConfirmReplace}>Replace &amp; re-import</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* File upload */}
      <Card className="p-8 mb-6 border-dashed border-2">
        <label className="flex flex-col items-center gap-3 cursor-pointer" data-testid="upload-label">
          <Upload className="w-10 h-10 text-accent"/>
          <div className="font-medium">{uploading ? "Processing..." : "Click to upload PDF, CSV, XLSX, or JPEG"}</div>
          <div className="text-sm text-muted-foreground">Text files are indexed for retrieval. JPEGs (≤1 MB) are stored as resource images.</div>
          <input type="file" accept=".pdf,.csv,.xlsx,.xls,.jpg,.jpeg,image/jpeg" className="hidden" onChange={onUpload} disabled={uploading} data-testid="upload-input"/>
          {uploading && <Loader2 className="w-5 h-5 animate-spin"/>}
        </label>
      </Card>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-xl font-semibold">Your documents</h2>
            {storage && (() => {
              const usedMB = (storage.used_bytes / (1024 * 1024)).toFixed(2);
              const quotaMB = (storage.quota_bytes / (1024 * 1024)).toFixed(0);
              const pct = Math.min(100, Math.round((storage.used_bytes / storage.quota_bytes) * 100));
              const nearFull = pct >= 85;
              return (
                <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground" data-testid="resources-storage">
                  <span className={nearFull ? "text-amber-700 font-medium" : ""}>{usedMB} MB / {quotaMB} MB used</span>
                  <div className="w-32 h-1.5 bg-muted rounded overflow-hidden">
                    <div className={`h-full ${nearFull ? "bg-amber-500" : "bg-accent"}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })()}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEditorMode("manual");
              setEditingDocId("");
              setEditingTitle("");
              setEditorOpen(true);
            }}
            data-testid="resources-create-manual"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Create manual document
          </Button>
        </div>
        {docs.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">No documents yet. Upload one above, import your website, or create a manual document.</div>
        ) : (
          // Tenant Intelligence prep — fixed-height scroll area shows ~4 rows
          // at a time so the page stays compact as resources scale. The sticky
          // header keeps column labels visible while the body scrolls. No
          // behavioral change — upload, edit, preview, download, rename, and
          // delete actions all flow through DocumentRow as before.
          <div
            className="max-h-[340px] overflow-y-auto rounded-md border bg-background/60"
            data-testid="resources-scroll-area"
          >
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10 shadow-[inset_0_-1px_0_0_var(--border)]"><TableRow><TableHead className="w-6"></TableHead><TableHead>File · Doc ID</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead>Pages</TableHead><TableHead>Chunks</TableHead><TableHead>Uploaded</TableHead><TableHead className="text-right">Assign · Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {docs.map(d => (
                  <DocumentRow
                    key={d.id}
                    doc={d}
                    onDelete={del}
                    onPreview={openPreview}
                    onDownload={downloadDoc}
                    onRename={openRename}
                    onAssignmentChange={() => { load(); loadAssignments(); }}
                    assignmentMapping={assignments.byDoc[d.id]}
                    assistantsList={assignments.assistants}
                    onEdit={(doc) => {
                      setEditorMode("edit");
                      setEditingDocId(doc.id);
                      setEditingTitle(doc.title || doc.filename || "");
                      setEditorOpen(true);
                    }}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* ============================================================== */}
      {/* Proposed Q&A v1 — tenant-scoped buyer Q&A list with             */}
      {/* Approve / Reject / Edit actions. No RAG ingestion in this step. */}
      {/* ============================================================== */}
      <ProposedQASection />

      <DocumentEditorModal
        open={editorOpen}
        mode={editorMode}
        docId={editingDocId}
        initialTitle={editingTitle}
        onClose={() => setEditorOpen(false)}
        onSaved={() => load()}
      />

      {/* Preview dialog — images render via Object URL; text docs render inline. */}
      {previewDoc && (
        <div data-testid="doc-preview-modal" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePreview(); }}>
          <div className="bg-background w-full max-w-3xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col overflow-hidden border">
            <div className="px-5 py-3.5 border-b flex items-center justify-between gap-3 bg-card">
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{previewDoc.title || previewDoc.filename}</div>
                {previewDoc.doc_code && <div className="text-[10px] font-mono text-muted-foreground">{previewDoc.doc_code}</div>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => downloadDoc(previewDoc)} data-testid="doc-preview-download"><Download className="w-3.5 h-3.5 mr-1.5"/>Download</Button>
                <Button size="sm" variant="ghost" onClick={closePreview} data-testid="doc-preview-close"><X className="w-4 h-4"/></Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-muted/30 grid place-items-center p-4">
              {previewLoading ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> :
                previewBlobUrl ? <img src={previewBlobUrl} alt={previewDoc.title} className="max-w-full max-h-full object-contain rounded shadow-sm" /> :
                previewText ? <div className="w-full max-h-full overflow-auto bg-background p-4 rounded border font-mono text-sm whitespace-pre-wrap leading-relaxed">{previewText}</div> :
                <div className="text-muted-foreground text-sm">No preview available.</div>}
            </div>
          </div>
        </div>
      )}

      {/* Rename dialog */}
      {renameDoc && (
        <div data-testid="doc-rename-modal" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget && !renaming) setRenameDoc(null); }}>
          <div className="bg-background w-full max-w-md rounded-xl shadow-2xl border p-5">
            <div className="font-semibold text-sm mb-1">Edit filename</div>
            <div className="text-xs text-muted-foreground mb-3">Document ID <span className="font-mono">{renameDoc.doc_code || "—"}</span> stays the same.</div>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="New filename / title"
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              autoFocus
              maxLength={255}
              data-testid="doc-rename-input"
              onKeyDown={(e) => { if (e.key === "Enter") submitRename(); }}
            />
            <div className="text-[11px] text-muted-foreground mt-1">Filename is mandatory and applies to this document only.</div>
            <div className="flex justify-end gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => setRenameDoc(null)} disabled={renaming} data-testid="doc-rename-cancel">Cancel</Button>
              <Button size="sm" onClick={submitRename} disabled={renaming || !renameValue.trim()} data-testid="doc-rename-save">
                {renaming ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>Saving…</> : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastFetched, setLastFetched] = useState(null);
  const navigate = useNavigate();
  // Filters / sort
  const [fSource, setFSource] = useState("");
  const [fStatus, setFStatus] = useState("");
  // Phase 1 Customer Foundation — lifecycle filter (lead/prospect/lost)
  const [fLeadStatus, setFLeadStatus] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get("lead_status") || "";
    } catch (_e) { return ""; }
  });
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [sortBy, setSortBy] = useState("date_added");
  const [sortDir, setSortDir] = useState("desc");
  // Dialogs
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showDetail, setShowDetail] = useState(null);

  const STATUS_OPTS = ["High", "Medium", "Low", "Marketing", "Lost", "Junk"];
  const SOURCE_OPTS = ["Sales Assistant", "Manual", "Imported"];

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fSource) params.set("source", fSource);
      if (fStatus) params.set("status", fStatus);
      if (fLeadStatus) params.set("lead_status", fLeadStatus);
      if (fFrom) params.set("date_from", fFrom);
      if (fTo) params.set("date_to", fTo);
      if (sortBy) params.set("sort_by", sortBy);
      if (sortDir) params.set("sort_dir", sortDir);
      const { data } = await api.get(`/leads?${params.toString()}`);
      setLeads(data);
      setLastFetched(new Date());
    } catch (err) {
      console.error("[leads] fetch failed", err);
      if (err?.response?.status !== 401) toast.error(err.response?.data?.detail || "Failed to load leads — please try again.");
    } finally { setLoading(false); }
  }, [fSource, fStatus, fLeadStatus, fFrom, fTo, sortBy, sortDir]);
  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const downloadFile = async (url, filename) => {
    try {
      const resp = await api.get(url, { responseType: "blob" });
      const u = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = u; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 1000);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error(err.response?.data?.detail || "Download failed");
    }
  };
  const downloadTemplate = () => downloadFile("/leads/template", "leads-template.xlsx");
  const exportLeads = () => {
    const params = new URLSearchParams();
    if (fSource) params.set("source", fSource);
    if (fStatus) params.set("status", fStatus);
    if (fFrom) params.set("date_from", fFrom);
    if (fTo) params.set("date_to", fTo);
    downloadFile(`/leads/export?${params.toString()}`, `leads-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const statusBadge = (s) => {
    const map = {
      High:      "bg-emerald-100 text-emerald-800 border-emerald-200",
      Medium:    "bg-amber-50 text-amber-800 border-amber-200",
      Low:       "bg-slate-100 text-slate-700 border-slate-200",
      Marketing: "bg-sky-50 text-sky-800 border-sky-200",
      Lost:      "bg-rose-50 text-rose-700 border-rose-200",
      Junk:      "bg-zinc-100 text-zinc-600 border-zinc-200",
    };
    return <Badge variant="outline" className={map[s] || ""}>{s || "—"}</Badge>;
  };

  const onLeadNameClick = (l) => {
    if (l.source === "Sales Assistant" && l.session_id) {
      navigate(`/dashboard/conversations?session=${encodeURIComponent(l.session_id)}`);
    } else {
      setShowDetail(l);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <h1 className="font-display text-4xl font-semibold">Leads</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="leads-template-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1.5"/>Template
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowImport(true)} data-testid="leads-import-btn">
            <Upload className="w-4 h-4 mr-1.5"/>Import
          </Button>
          <Button size="sm" variant="outline" onClick={exportLeads} data-testid="leads-export-btn">
            <Download className="w-4 h-4 mr-1.5"/>Export
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="leads-add-btn">
            <Plus className="w-4 h-4 mr-1.5"/>Add Lead
          </Button>
          <Button size="sm" variant="ghost" onClick={fetchLeads} disabled={loading} data-testid="leads-refresh" aria-label="Refresh leads">
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
          </Button>
        </div>
      </div>
      <p className="text-muted-foreground mb-4">
        Captured leads with stable Lead IDs, source/type, and status. Click a Sales Assistant lead name to open the related conversation.
        {lastFetched && <span className="text-xs ml-2">· Updated {lastFetched.toLocaleTimeString()}</span>}
      </p>

      <Card className="p-3 mb-4 flex flex-wrap items-center gap-2">
        <select className="text-sm border rounded-md px-2 py-1.5 bg-background" value={fSource} onChange={e => setFSource(e.target.value)} data-testid="leads-filter-source">
          <option value="">All sources</option>
          {SOURCE_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="text-sm border rounded-md px-2 py-1.5 bg-background" value={fStatus} onChange={e => setFStatus(e.target.value)} data-testid="leads-filter-status">
          <option value="">All status</option>
          {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="text-sm border rounded-md px-2 py-1.5 bg-background" value={fLeadStatus} onChange={e => setFLeadStatus(e.target.value)} data-testid="leads-filter-lead-status" title="Lifecycle: Lead vs Prospect vs Lost">
          <option value="">All lifecycle</option>
          <option value="lead">Leads</option>
          <option value="prospect">Prospects</option>
          <option value="lost">Lost</option>
        </select>
        <input type="date" className="text-sm border rounded-md px-2 py-1.5 bg-background" value={fFrom?.slice(0,10) || ""} onChange={e => setFFrom(e.target.value)} data-testid="leads-filter-from" title="Date from" />
        <input type="date" className="text-sm border rounded-md px-2 py-1.5 bg-background" value={fTo?.slice(0,10) || ""} onChange={e => setFTo(e.target.value ? `${e.target.value}T23:59:59Z` : "")} data-testid="leads-filter-to" title="Date to" />
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <span>Sort:</span>
          <select className="text-sm border rounded-md px-2 py-1.5 bg-background" value={sortBy} onChange={e => setSortBy(e.target.value)} data-testid="leads-sort-by">
            <option value="date_added">Date Added</option>
            <option value="source">Source</option>
            <option value="status">Status</option>
            <option value="lead_name">Lead Name</option>
          </select>
          <select className="text-sm border rounded-md px-2 py-1.5 bg-background" value={sortDir} onChange={e => setSortDir(e.target.value)} data-testid="leads-sort-dir">
            <option value="desc">↓ Desc</option>
            <option value="asc">↑ Asc</option>
          </select>
          {(fSource || fStatus || fFrom || fTo) && (
            <button className="text-xs underline" onClick={() => { setFSource(""); setFStatus(""); setFFrom(""); setFTo(""); }} data-testid="leads-clear-filters">Clear filters</button>
          )}
        </div>
      </Card>

      <Card className="p-0 overflow-hidden" data-testid="leads-card">
        {loading && leads.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin"/> Loading leads…
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-16 px-6 text-muted-foreground" data-testid="leads-empty">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-40"/>
            <div className="font-medium text-foreground mb-1">No leads yet</div>
            <div className="text-sm max-w-sm mx-auto">
              Share your chatbot link, import a spreadsheet, or click <strong>Add Lead</strong> to create one manually.
            </div>
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Lead ID</TableHead><TableHead>Date Added</TableHead><TableHead>Lead Name</TableHead><TableHead>Product</TableHead><TableHead>Source</TableHead><TableHead>Status</TableHead><TableHead data-testid="leads-col-lifecycle">Lifecycle</TableHead><TableHead>Comments</TableHead></TableRow></TableHeader>
            <TableBody>
              {leads.map(l => (
                <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                  <TableCell className="font-mono text-xs">{l.lead_code || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{(l.date_added || l.created_at) ? new Date(l.date_added || l.created_at).toLocaleString() : "—"}</TableCell>
                  <TableCell>
                    <button className="font-medium text-left hover:text-accent transition-colors inline-flex items-center gap-1 group" onClick={() => onLeadNameClick(l)} data-testid={`lead-name-${l.id}`}>
                      <span>{l.lead_name || l.name || "—"}</span>
                      {l.source === "Sales Assistant" && l.session_id && <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-70 transition-opacity"/>}
                    </button>
                  </TableCell>
                  <TableCell className="text-sm">{l.product || "—"}</TableCell>
                  <TableCell><Badge variant="secondary">{l.source || "—"}</Badge></TableCell>
                  <TableCell>{statusBadge(l.status)}</TableCell>
                  <TableCell>
                    <select
                      value={l.lead_status || "lead"}
                      onChange={async (e) => {
                        const v = e.target.value;
                        try {
                          await api.patch(`/leads/${l.id}/lead-status`, { lead_status: v });
                          setLeads(prev => prev.map(x => x.id === l.id ? { ...x, lead_status: v } : x));
                        } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs border rounded px-1.5 py-0.5 bg-background"
                      data-testid={`lead-lifecycle-${l.id}`}
                    >
                      <option value="lead">Lead</option>
                      <option value="prospect">Prospect</option>
                      <option value="lost">Lost</option>
                    </select>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-md truncate" title={l.comments || l.message || ""}>{l.comments || l.message || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {showAdd && <AddLeadDialog onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); fetchLeads(); }} statusOpts={STATUS_OPTS} />}
      {showImport && <ImportLeadsDialog onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); fetchLeads(); }} onTemplate={downloadTemplate} />}
      {showDetail && <LeadDetailDialog lead={showDetail} onClose={() => setShowDetail(null)} />}
    </DashboardLayout>
  );
}

function AddLeadDialog({ onClose, onSaved, statusOpts }) {
  const [leadName, setLeadName] = useState("");
  const [product, setProduct] = useState("");
  const [status, setStatus] = useState("Medium");
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!leadName.trim()) { toast.error("Lead Name is required."); return; }
    if (!product.trim()) { toast.error("Product is required."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/leads/manual", { lead_name: leadName.trim(), product: product.trim(), status, comments: comments.trim() });
      toast.success(`Lead ${data.lead_code} created`);
      onSaved();
    } catch (err) {
      if (err?.response?.status !== 401) toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <div data-testid="add-lead-modal" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="bg-background w-full max-w-md rounded-xl shadow-2xl border p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="font-semibold">Add Lead</div>
          <button onClick={onClose} disabled={saving}><X className="w-4 h-4"/></button>
        </div>
        <div className="text-xs text-muted-foreground mb-4">Lead ID is auto-generated on save and never changes.</div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Lead ID</Label>
            <div className="text-sm font-mono text-muted-foreground border rounded px-2 py-1.5 bg-muted/30">Auto-generated on save</div>
          </div>
          <div>
            <Label className="text-xs">Lead Name<span className="text-red-600">*</span></Label>
            <Input value={leadName} onChange={e => setLeadName(e.target.value)} maxLength={255} data-testid="add-lead-name" autoFocus/>
          </div>
          <div>
            <Label className="text-xs">Product<span className="text-red-600">*</span></Label>
            <Input value={product} onChange={e => setProduct(e.target.value)} maxLength={255} data-testid="add-lead-product"/>
          </div>
          <div>
            <Label className="text-xs">Source</Label>
            <div className="text-sm border rounded px-2 py-1.5 bg-muted/30">Manual <span className="text-xs text-muted-foreground ml-1">(not editable)</span></div>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <select className="w-full text-sm border rounded-md px-2 py-1.5 bg-background" value={status} onChange={e => setStatus(e.target.value)} data-testid="add-lead-status">
              {statusOpts.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Comments</Label>
            <textarea className="w-full text-sm border rounded-md px-2 py-1.5 bg-background" rows={3} value={comments} onChange={e => setComments(e.target.value)} data-testid="add-lead-comments"/>
          </div>
          <div>
            <Label className="text-xs">Date Added</Label>
            <div className="text-sm text-muted-foreground">System-generated on save</div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="add-lead-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !leadName.trim() || !product.trim()} data-testid="add-lead-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Saving…</> : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ImportLeadsDialog({ onClose, onDone, onTemplate }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/leads/import", fd);
      setResult(data);
      const okMsg = `Imported ${data.inserted}` + (data.skipped_duplicates ? ` · skipped ${data.skipped_duplicates} duplicate${data.skipped_duplicates === 1 ? "" : "s"}` : "");
      toast.success(okMsg);
      if (!data.errors?.length) setTimeout(onDone, 1000);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error(err.response?.data?.detail || "Import failed");
    } finally { setUploading(false); }
  };
  return (
    <div data-testid="import-leads-modal" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget && !uploading) onClose(); }}>
      <div className="bg-background w-full max-w-lg rounded-xl shadow-2xl border p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold">Import Leads (Excel)</div>
          <button onClick={onClose} disabled={uploading}><X className="w-4 h-4"/></button>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Upload an XLSX file with exactly these columns: <strong>Lead Name, Product, Status, Comments</strong>.
          <button className="underline ml-1" onClick={onTemplate}>Download template</button>.
        </p>
        <input type="file" accept=".xlsx" onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="import-leads-file"/>
        {result && (
          <div className="mt-3 text-sm space-y-1" data-testid="import-leads-result">
            <div className="text-emerald-700">Inserted: <strong>{result.inserted}</strong></div>
            {result.skipped_duplicates > 0 && <div className="text-amber-700">Skipped duplicates: <strong>{result.skipped_duplicates}</strong></div>}
            {result.errors?.length > 0 && (
              <div className="text-rose-700">
                <div className="font-medium mb-1">Row errors ({result.errors.length}):</div>
                <ul className="list-disc list-inside text-xs space-y-0.5 max-h-32 overflow-auto">
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} disabled={uploading} data-testid="import-leads-cancel">Close</Button>
          <Button onClick={submit} disabled={uploading || !file} data-testid="import-leads-submit">
            {uploading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Importing…</> : "Import"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function LeadDetailDialog({ lead, onClose }) {
  return (
    <div data-testid="lead-detail-modal" className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-background w-full max-w-lg rounded-xl shadow-2xl border p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-semibold">{lead.lead_name || lead.name}</div>
            <div className="text-xs font-mono text-muted-foreground">{lead.lead_code}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4"/></button>
        </div>
        <dl className="text-sm space-y-2">
          {[
            ["Source", lead.source],
            ["Status", lead.status],
            ["Product", lead.product],
            ["Date Added", lead.date_added || lead.created_at],
            ["Email", lead.email],
            ["Phone", lead.phone],
            ["Comments", lead.comments || lead.message],
            ["Session ID", lead.session_id],
          ].filter(([, v]) => v).map(([k, v]) => (
            <div key={k} className="grid grid-cols-3 gap-2">
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="col-span-2 break-words">{String(v)}</dd>
            </div>
          ))}
        </dl>
        <div className="flex justify-end mt-4"><Button variant="outline" size="sm" onClick={onClose}>Close</Button></div>
      </div>
    </div>
  );
}

export function Conversations() {
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => { api.get("/conversations").then(r => setConvs(r.data)); }, []);
  return (
    <DashboardLayout>
      <h1 className="font-display text-4xl font-semibold mb-2">Conversations</h1>
      <p className="text-muted-foreground mb-8">Full transcripts from your public chatbot.</p>
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-0 lg:col-span-1 max-h-[70vh] overflow-y-auto">
          {convs.length === 0 ? <div className="p-6 text-muted-foreground text-sm">No conversations yet.</div> :
            convs.map(c => {
              const si = c.sales_state || {};
              return (
                <button key={c.id} onClick={() => setSelected(c)} className={`w-full text-left p-4 border-b hover:bg-secondary transition ${selected?.id === c.id ? "bg-secondary" : ""}`} data-testid={`conv-row-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-medium truncate flex-1">{c.messages?.[0]?.content?.slice(0, 60) || "—"}</div>
                    <LeadScorePill score={si.score} classification={si.classification} testId={`conv-score-${c.id}`}/>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{c.messages?.length || 0} messages · {new Date(c.updated_at).toLocaleDateString()}</div>
                  <div className="mt-1"><QualSummary qualification={c.qualification}/></div>
                </button>
              );
            })
          }
        </Card>
        <Card className="lg:col-span-2 p-6 max-h-[70vh] overflow-y-auto">
          {!selected ? <div className="text-muted-foreground text-center py-16">Select a conversation</div> :
            <div>
              {(selected.sales_state || selected.qualification) && (
                <div className="mb-4 p-3 rounded-lg bg-muted/40 border flex flex-wrap gap-3 items-center" data-testid="conv-intel-summary">
                  <LeadScorePill score={selected.sales_state?.score} classification={selected.sales_state?.classification}/>
                  <QualSummary qualification={selected.qualification}/>
                  {selected.sales_state?.signals_seen?.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      signals: {selected.sales_state.signals_seen.filter(s => !s.startsWith("_")).slice(0, 5).join(", ")}
                    </span>
                  )}
                </div>
              )}
              <div className="space-y-3">
                {selected.messages?.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground rounded-tr-sm" : "bg-secondary rounded-tl-sm"}`}>{m.content}</div>
                  </div>
                ))}
              </div>
            </div>
          }
        </Card>
      </div>
    </DashboardLayout>
  );
}

export function UsageBilling() {
  const [s, setS] = useState(null);
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/usage").then(r => setS(r.data));
    api.get("/company").then(r => setInfo(r.data));
  }, []);
  // v1.0.17 — subscription-first language. Tokens are an internal metric
  // and confuse retail/SMB users, so the customer-facing surface talks
  // about Usage / Conversations / Messages / Trial days remaining instead.
  const co = info?.company || {};
  const plan = (co.subscription_plan || "trial").toLowerCase();
  const status = co.subscription_status || "active";
  const trialDays = (() => {
    if (plan !== "trial") return null;
    const ends = co.trial_ends_at || co.trial_expires_at;
    if (!ends) return null;
    const diff = Math.max(0, Math.ceil((new Date(ends) - new Date()) / (1000 * 60 * 60 * 24)));
    return diff;
  })();
  const planLabel = plan === "trial"
    ? `Trial${trialDays != null ? ` · ${trialDays} day${trialDays === 1 ? "" : "s"} left` : ""}`
    : plan.charAt(0).toUpperCase() + plan.slice(1);
  return (
    <DashboardLayout>
      <h1 className="font-display text-4xl font-semibold mb-2">Usage & Billing</h1>
      <p className="text-muted-foreground mb-8">Your subscription, monthly usage, and conversation activity.</p>
      <UsageBanner />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <StatCard
          icon={CreditCard}
          label="Current plan"
          value={planLabel}
          hint={status === "active" ? "Active" : status}
        />
        <StatCard
          icon={MessagesSquare}
          label="Conversations this month"
          value={s?.conversation_count ?? s?.message_count ?? "—"}
          hint={s?.message_count != null ? `${s.message_count} messages` : null}
        />
        <StatCard
          icon={BarChart3}
          label="Usage this month"
          value={`${s?.usage_percent ?? 0}%`}
          hint="of monthly limit"
        />
      </div>
      <Card className="p-6">
        <div className="flex justify-between items-baseline mb-2">
          <div className="font-medium">Monthly usage limit</div>
          <div className="text-sm text-muted-foreground">{s?.usage_percent || 0}%</div>
        </div>
        <div className="w-full h-3 bg-secondary rounded-full overflow-hidden">
          <div className="h-full bg-accent rounded-full transition-all" style={{ width: `${s?.usage_percent || 0}%` }}/>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          {plan === "trial"
            ? "Trial includes a monthly limit on AI usage. Upgrade your plan to unlock more conversations."
            : "Your subscription includes a monthly limit. Heavier usage automatically upgrades or top-ups depending on your plan."}
        </p>
      </Card>
    </DashboardLayout>
  );
}

// =====================================================================
// Stripe Production Readiness sprint (Feb 2026) — Mode Badge + Env Panel
// ---------------------------------------------------------------------
// Reads GET /api/billing/health (already exposed by routes/billing_stripe.py)
// and renders a single source of truth for "which Stripe mode is this
// environment running in" so operators don't have to inspect env vars
// to find out. Never displays a full key — only the last-4 chars (same
// surface Stripe's own dashboard shows). The panel intentionally has
// NO write actions; the mode switch is owned by ops (env vars on the
// container), not the in-app UI. Live-mode is gated by a server-side
// allow-list flag (STRIPE_ALLOW_LIVE_MODE) — the panel explains how to
// flip it so the operator doesn't accidentally process real payments.
// =====================================================================

function _stripeMode(health) {
  const t = health?.stripe_key_type;
  if (t === "live") return "live";
  if (t === "test") return "test";
  return "unknown";
}

export function StripeModeBadge({ health }) {
  const mode = _stripeMode(health);
  if (!health) {
    return null; // health not loaded yet — render nothing rather than a flicker
  }
  if (mode === "live") {
    return (
      <span data-testid="stripe-mode-badge"
            data-stripe-mode="live"
            className="px-2 py-1 rounded text-xs font-semibold uppercase tracking-wide
                       bg-rose-100 text-rose-800 border border-rose-200">
        Stripe · LIVE
      </span>
    );
  }
  if (mode === "test") {
    return (
      <span data-testid="stripe-mode-badge"
            data-stripe-mode="test"
            className="px-2 py-1 rounded text-xs font-semibold uppercase tracking-wide
                       bg-amber-100 text-amber-800 border border-amber-200">
        Stripe · TEST
      </span>
    );
  }
  return (
    <span data-testid="stripe-mode-badge"
          data-stripe-mode="unknown"
          className="px-2 py-1 rounded text-xs font-semibold uppercase tracking-wide
                     bg-slate-100 text-slate-700 border border-slate-200">
      Stripe · NOT CONFIGURED
    </span>
  );
}

export function StripeEnvironmentPanel({ health }) {
  // Confirmation modal state — operators must explicitly acknowledge
  // the LIVE-mode warning before we let them dismiss it. This is the
  // "Add safe warning before enabling live mode" requirement from the
  // Feb 2026 sprint.
  const [ackOpen, setAckOpen] = useState(false);

  if (!health) {
    return (
      <div data-testid="stripe-env-panel-loading"
           className="mb-6 px-4 py-3 rounded-lg text-xs text-muted-foreground border border-slate-200 bg-slate-50">
        Loading Stripe environment status…
      </div>
    );
  }

  const mode = _stripeMode(health);
  const env = health.env || {};
  const secret = env.STRIPE_SECRET_KEY || {};
  const wh = env.STRIPE_WEBHOOK_SECRET || {};
  const allowLive = !!env.STRIPE_ALLOW_LIVE_MODE?.value;
  const liveBlocked = !!health.live_blocked;
  const source = health.stripe_key_source || "—";

  // Pick container styling by mode.
  const containerCls =
    mode === "live"
      ? "bg-rose-50 border-rose-200"
      : mode === "test"
      ? "bg-amber-50 border-amber-200"
      : "bg-slate-50 border-slate-200";

  return (
    <div data-testid="stripe-env-panel"
         data-stripe-mode={mode}
         className={`mb-6 px-4 py-3 rounded-lg text-sm border ${containerCls}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          {mode === "live" && (
            <div className="font-semibold text-rose-900" data-testid="stripe-live-banner">
              ⚠ Live Stripe mode is enabled — real payments WILL be processed.
            </div>
          )}
          {mode === "test" && (
            <div className="font-semibold text-amber-900" data-testid="stripe-test-banner">
              Stripe is in TEST mode. No real money will move — use Stripe test cards.
            </div>
          )}
          {mode === "unknown" && (
            <div className="font-semibold text-slate-800" data-testid="stripe-unconfigured-banner">
              Stripe is NOT configured on this environment. Set <code>STRIPE_SECRET_KEY</code> to enable payments.
            </div>
          )}
          {liveBlocked && (
            <div className="text-xs text-rose-900" data-testid="stripe-live-blocked-banner">
              A live Stripe key was detected but <code>STRIPE_ALLOW_LIVE_MODE</code> is not set —
              the platform is intentionally running with no Stripe key until live mode is acknowledged.
            </div>
          )}
          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 pt-1">
            <span>Source: <code data-testid="stripe-key-source">{source || "—"}</code></span>
            <span>Key suffix: <code data-testid="stripe-key-suffix">…{secret.suffix || "—"}</code></span>
            <span>Webhook: <code data-testid="stripe-webhook-status">{wh.present ? `…${wh.suffix}` : "missing"}</code></span>
            <span>Allow live: <code data-testid="stripe-allow-live">{allowLive ? "yes" : "no"}</code></span>
          </div>
        </div>
        <button
          type="button"
          data-testid="stripe-live-mode-help-btn"
          className="text-xs px-3 py-1.5 rounded border border-slate-300 bg-white hover:bg-slate-50"
          onClick={() => setAckOpen(true)}
        >
          How to enable LIVE mode
        </button>
      </div>

      {ackOpen && (
        <div data-testid="stripe-live-mode-help-modal"
             className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-lg border border-slate-200">
            <div className="px-5 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-rose-900">Enable Stripe Live Mode</h2>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm">
              <p className="font-semibold text-rose-900">
                ⚠ Live Stripe mode will process real payments and move real money.
              </p>
              <p className="text-slate-700">
                Mode switching is owned by ops (not the in-app UI) so a misclick can
                never accidentally bill a real card. To enable LIVE mode, an admin
                with shell access must:
              </p>
              <ol className="list-decimal pl-5 space-y-1 text-slate-700">
                <li>Set <code className="bg-slate-100 px-1 rounded">STRIPE_SECRET_KEY</code> to your <code>sk_live_…</code> key.</li>
                <li>Set <code className="bg-slate-100 px-1 rounded">STRIPE_ALLOW_LIVE_MODE=true</code>.</li>
                <li>Set <code className="bg-slate-100 px-1 rounded">STRIPE_WEBHOOK_SECRET</code> to your <em>live</em> webhook secret.</li>
                <li>Restart the backend (<code className="bg-slate-100 px-1 rounded">sudo supervisorctl restart backend</code>).</li>
                <li>Reload this page — the badge should turn <span className="font-semibold text-rose-700">red &middot; LIVE</span>.</li>
              </ol>
              <p className="text-xs text-slate-500 pt-1">
                The safety guard refuses to load an <code>sk_live_*</code> key unless
                <code> STRIPE_ALLOW_LIVE_MODE</code> is explicitly true. Forgetting that
                flag intentionally disables payments rather than silently going live.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
              <button
                type="button"
                data-testid="stripe-live-mode-help-close"
                onClick={() => setAckOpen(false)}
                className="px-3 py-1.5 rounded bg-slate-800 text-white text-sm hover:bg-slate-700"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Iter64 (Feb 2026) — Stripe Subscription Billing Foundation.
// Replaced the legacy emergentintegrations one-time-Checkout flow with
// real subscriptions via the new /api/billing/* endpoints. The component
// is still exported as `Subscription` so the existing /dashboard/subscription
// route and sidebar link keep working; the page now functions as the
// canonical "Settings → Billing" surface (Iter64 spec).
export function Subscription() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(null);  // 'starter' | 'professional' | 'portal' | 'trial' | null
  const [pollMsg, setPollMsg] = useState(null);  // {message, type} | null
  const [payments, setPayments] = useState(null);  // null = not loaded, [] = loaded empty
  // Stripe Production Readiness sprint (Feb 2026) — read environment
  // health so the operator can see at a glance which mode the platform
  // is running in (test/live), whether the webhook is configured, and
  // whether STRIPE_ALLOW_LIVE_MODE has dropped a key.
  const [stripeHealth, setStripeHealth] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/billing/status");
      setStatus(data);
    } catch (e) {
      toast.error("Failed to load billing status.");
    } finally {
      setLoading(false);
    }
    // Iter64.5 — fetch Payment History in parallel. Safe to fail
    // silently (the section just won't render if it errors).
    try {
      const { data } = await api.get("/billing/payments");
      setPayments(data.payments || []);
    } catch { setPayments([]); }
    // Stripe Production Readiness — health is purely informational and
    // public (only present/absent + last-4 chars per the contract on
    // `/api/billing/health`). Best-effort fetch; UI degrades gracefully.
    try {
      const { data } = await api.get("/billing/health");
      setStripeHealth(data);
    } catch { setStripeHealth(null); }
  };
  useEffect(() => { load(); }, []);

  // Post-Checkout return — clean ?session_id / ?cancelled from URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("cancelled")) {
      setPollMsg({ message: "Payment cancelled. You can try again any time.", type: "error" });
      // Iter64.3 — preserve whichever path the user landed on (/dashboard/billing or
      // /dashboard/subscription) so we don't bounce between the alias and the canonical.
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (params.get("session_id")) {
      setPollMsg({ message: "Payment received — subscription activating…", type: "pending" });
      window.history.replaceState({}, "", window.location.pathname);
      // Stripe webhook updates `companies.billing` asynchronously; poll
      // /billing/status until we see a non-trialing status or we've
      // tried 6 times.
      let attempts = 0;
      const tick = async () => {
        attempts += 1;
        try {
          const { data } = await api.get("/billing/status");
          setStatus(data);
          if (["active", "trialing"].includes(data.subscription_status) && data.stripe_subscription_id) {
            setPollMsg({ message: "Subscription active!", type: "success" });
            toast.success("Subscription active");
            return;
          }
        } catch {}
        if (attempts < 6) setTimeout(tick, 2500);
        else {
          // Iter64.7 — last-resort auto-sync. Webhook may not be
          // configured yet (operator-side); sync-from-stripe rebuilds
          // the mirror by reading Stripe directly. Idempotent + safe.
          try {
            await api.post("/billing/sync-from-stripe");
            const { data } = await api.get("/billing/status");
            setStatus(data);
            if (["active", "trialing"].includes(data.subscription_status) && data.stripe_subscription_id) {
              setPollMsg({ message: "Subscription active!", type: "success" });
              toast.success("Subscription active");
              return;
            }
          } catch {}
          setPollMsg({ message: "Still processing — click 'Sync from Stripe' or refresh in a moment.", type: "pending" });
        }
      };
      tick();
    }
  }, []);

  const startTrial = async () => {
    setActionBusy("trial");
    try {
      await api.post("/billing/start-trial");
      toast.success("14-day free trial started.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start trial.");
    } finally { setActionBusy(null); }
  };

  const upgrade = async (planCode) => {
    setActionBusy(planCode);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_code: planCode,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.error("No checkout URL returned.");
    } catch (e) {
      // Iter64.6 — server-side duplicate guard. Friendly UX: bounce
      // the user to the portal instead of just toasting an error.
      if (e.response?.status === 409 && e.response?.data?.detail?.manage_billing) {
        toast.info("You already have an active subscription. Opening Manage Billing…");
        await openPortal();
        return;
      }
      toast.error(e.response?.data?.detail?.message
                   || e.response?.data?.detail
                   || "Checkout failed.");
      setActionBusy(null);
    }
  };

  const syncFromStripe = async () => {
    setActionBusy("sync");
    try {
      const { data } = await api.post("/billing/sync-from-stripe");
      const dupMsg = data.duplicate_subscriptions
        ? ` ⚠ ${data.duplicates.length} duplicate subscriptions detected — use Manage Billing to cancel extras.`
        : "";
      toast.success(`Synced ${data.synced_subscriptions} subscription, ${data.synced_invoices} invoices.${dupMsg}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed.");
    } finally { setActionBusy(null); }
  };

  const openPortal = async () => {
    setActionBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (data.portal_url) window.open(data.portal_url, "_blank", "noopener");
      else toast.error("No portal URL.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Portal failed.");
    } finally { setActionBusy(null); }
  };

  if (loading) {
    return <DashboardLayout><div className="p-8 text-sm text-muted-foreground" data-testid="billing-loading">Loading billing…</div></DashboardLayout>;
  }
  const s = status || {};
  const plans = s.plans || {};
  const isPastDue = s.is_past_due;
  const planCode = s.plan_code || "trial";
  const subStatus = s.subscription_status || "none";
  const isPaid = ["starter", "professional"].includes(planCode) && ["active", "trialing"].includes(subStatus);
  const isTrialing = subStatus === "trialing" && planCode === "trial";
  const showStartTrial = subStatus === "none" && !s.stripe_subscription_id;
  // Iter64.6 — true when the tenant already has a live Stripe sub.
  // The Upgrade buttons are hidden in this state to prevent duplicate
  // subscriptions (the backend also enforces this with a 409).
  const hasActiveSub = s.has_active_subscription === true;

  const statusBadgeColor = {
    active: "bg-emerald-100 text-emerald-800 border-emerald-200",
    trialing: "bg-sky-100 text-sky-800 border-sky-200",
    past_due: "bg-rose-100 text-rose-800 border-rose-200",
    canceled: "bg-slate-100 text-slate-700 border-slate-200",
    incomplete: "bg-amber-100 text-amber-800 border-amber-200",
    none: "bg-slate-100 text-slate-600 border-slate-200",
  }[subStatus] || "bg-slate-100 text-slate-600 border-slate-200";

  // -------------------------------------------------------------------
  // Iter64.13 — Scheduled-cancellation derivation.
  //
  // Bug seen in prod with sub_1Tj51rPcPtyKtzY9H20bIHiJ:
  //   `/api/billing/status` returned
  //   `{cancel_at_period_end: true, cancel_at: "2026-06-30T..."}`
  //   yet the UI still rendered "Renews on 6/30/2026" with no banner.
  //   Root cause: the page was relying on `s.cancel_at_period_end`
  //   ALONE (a boolean), so any stale/missing-boolean response — even
  //   transient ones from a webhook that wrote `cancel_at` BEFORE the
  //   boolean — fell back to the "Renews on" branch.
  //
  // Fix: derive a single source of truth for "is the subscription
  // scheduled to cancel" by considering BOTH signals:
  //   - explicit boolean `cancel_at_period_end === true`, OR
  //   - a `cancel_at` ISO timestamp in the future (Stripe's
  //     authoritative cancellation date — present whenever the
  //     subscription will end at a known time).
  // We also pre-compute the date string so the JSX is mechanical.
  // -------------------------------------------------------------------
  const cancelAtMs = s.cancel_at ? Date.parse(s.cancel_at) : NaN;
  const periodEndMs = s.current_period_end ? Date.parse(s.current_period_end) : NaN;
  const cancelDateMs = Number.isFinite(cancelAtMs) ? cancelAtMs
                      : Number.isFinite(periodEndMs) && s.cancel_at_period_end ? periodEndMs
                      : NaN;
  const isCancelScheduled = (s.cancel_at_period_end === true)
                            || (Number.isFinite(cancelAtMs) && cancelAtMs > Date.now());
  const cancelDateLabel = Number.isFinite(cancelDateMs)
                          ? new Date(cancelDateMs).toLocaleDateString()
                          : null;

  return (
    <DashboardLayout>
      <div className="flex items-baseline justify-between mb-1">
        <div className="flex items-baseline gap-3">
          <h1 className="font-display text-4xl font-semibold">Billing</h1>
          <StripeModeBadge health={stripeHealth} />
        </div>
        {s.customer_reference_id && (
          <span data-testid="billing-customer-reference-id"
                className="text-xs font-mono px-2 py-1 rounded bg-slate-100 text-slate-700 border border-slate-200">
            Customer ID: <strong>{s.customer_reference_id}</strong>
          </span>
        )}
      </div>
      <p className="text-muted-foreground mb-6 text-sm">Manage your Rudras-AI subscription and billing details.</p>

      {/* Stripe Production Readiness (Feb 2026) — environment safety
          panel. Always renders so operators see at a glance which
          mode is active. LIVE mode shows a red banner; TEST shows a
          neutral info card; missing/placeholder keys show an amber
          warning. */}
      <StripeEnvironmentPanel health={stripeHealth} />

      {!s.configured && (
        <div className="mb-6 px-4 py-3 rounded-lg text-sm bg-amber-50 text-amber-900 border border-amber-200" data-testid="billing-not-configured">
          Stripe billing isn't configured on this environment yet. Contact your administrator.
        </div>
      )}

      {pollMsg && (
        <div data-testid="billing-poll-msg" className={`mb-6 px-4 py-3 rounded-lg text-sm border ${pollMsg.type === "success" ? "bg-emerald-50 text-emerald-900 border-emerald-200" : pollMsg.type === "error" ? "bg-rose-50 text-rose-900 border-rose-200" : "bg-sky-50 text-sky-900 border-sky-200"}`}>{pollMsg.message}</div>
      )}

      {isPastDue && (
        <div data-testid="billing-past-due-banner" className="mb-6 px-4 py-3 rounded-lg text-sm bg-rose-50 text-rose-900 border border-rose-200">
          <strong>Payment failed.</strong> Your last invoice could not be charged. Please update your payment method via Manage Billing to keep your subscription active.
        </div>
      )}

      {/* Iter64.13 — scheduled-cancellation banner. Mirrors what the
          Stripe Billing Portal shows ("Cancels Jun 30"). Uses the
          derived `isCancelScheduled` / `cancelDateLabel` so we are
          immune to webhook ordering glitches where `cancel_at` is
          written before `cancel_at_period_end` (or vice versa). */}
      {isCancelScheduled && cancelDateLabel && (
        <div data-testid="billing-cancel-scheduled-banner"
             data-cancel-at={s.cancel_at || ""}
             data-cancel-at-period-end={String(!!s.cancel_at_period_end)}
             className="mb-6 px-4 py-3 rounded-lg text-sm bg-amber-50 text-amber-900 border border-amber-200">
          <strong>Subscription scheduled to cancel on {cancelDateLabel}.</strong> You'll keep access until then. To resume, click <strong>Manage Billing</strong> and choose "Don't cancel subscription".
        </div>
      )}

      {/* Iter64.6 — hint banner once duplicates are detected after a
          sync-from-stripe. We surface a small warning so the operator
          can clean up via the Portal. Hidden when a cancellation is
          already scheduled (the amber banner above takes priority). */}
      {hasActiveSub && !isCancelScheduled && (
        <div data-testid="billing-has-active-banner" className="mb-6 px-4 py-3 rounded-lg text-sm bg-emerald-50 text-emerald-900 border border-emerald-200">
          You have an active subscription. To switch plans, update payment method, or cancel — use <strong>Manage Billing</strong> above.
        </div>
      )}

      {/* ---- Current state card ---- */}
      <Card className="p-6 mb-6" data-testid="billing-current-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Current plan</div>
            <div className="font-display text-3xl font-semibold capitalize" data-testid="billing-plan-code">
              {planCode === "trial" ? "Free Trial" : (plans[planCode]?.name || planCode)}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusBadgeColor}`} data-testid="billing-status-badge">
                {subStatus.replace("_", " ")}
              </span>
              {isTrialing && s.trial_days_remaining !== null && (
                <span className="text-xs text-muted-foreground" data-testid="billing-trial-days-remaining">
                  {s.trial_days_remaining} day{s.trial_days_remaining === 1 ? "" : "s"} remaining in trial
                </span>
              )}
              {(s.current_period_end || s.cancel_at) && !isTrialing && (
                <span className="text-xs text-muted-foreground" data-testid="billing-period-end">
                  {isCancelScheduled ? "Cancels on " : "Renews on "}
                  {cancelDateLabel || new Date(s.current_period_end).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {(isPaid || s.stripe_customer_id) && (
              <Button variant="outline" onClick={syncFromStripe} disabled={actionBusy === "sync"} data-testid="billing-sync-btn"
                      title="Re-sync local billing mirror from Stripe (use if Payment history looks stale).">
                {actionBusy === "sync" ? "Syncing…" : "Sync from Stripe"}
              </Button>
            )}
            {(isPaid || s.stripe_customer_id) && (
              <Button variant="outline" onClick={openPortal} disabled={actionBusy === "portal"} data-testid="billing-manage-btn">
                {actionBusy === "portal" ? "Opening…" : "Manage Billing"}
              </Button>
            )}
            {showStartTrial && (
              <Button onClick={startTrial} disabled={actionBusy === "trial" || !s.configured} className="bg-primary hover:bg-primary/90" data-testid="billing-start-trial-btn">
                {actionBusy === "trial" ? "Starting…" : "Start 14-Day Free Trial"}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* ---- Plan tiles ---- */}
      <h2 className="font-display text-2xl font-semibold mb-3">Plans</h2>
      <div className="grid md:grid-cols-2 gap-4">
        {["starter", "professional"].map((code) => {
          const p = plans[code];
          if (!p) return null;
          const isCurrent = planCode === code;
          return (
            <Card key={code} className={`p-6 ${isCurrent ? "border-primary ring-2 ring-primary/20" : ""}`} data-testid={`billing-plan-${code}`}>
              <div className="flex items-start justify-between">
                <h3 className="font-display text-2xl font-semibold">{p.name}</h3>
                {isCurrent && <span className="text-[10px] uppercase tracking-wide bg-primary text-primary-foreground px-2 py-0.5 rounded-md">Current</span>}
              </div>
              <div className="font-display text-5xl font-semibold mt-2">
                ${p.amount}<span className="text-base text-muted-foreground">/{p.interval}</span>
              </div>
              <ul className="mt-4 space-y-1.5 text-sm">
                {code === "starter" && [
                  "1 AI chatbot",
                  "Lead capture + email transcripts",
                  "Standard support",
                ].map((x) => <li key={x} className="flex gap-2"><span className="text-emerald-600">✓</span>{x}</li>)}
                {code === "professional" && [
                  "Multiple AI assistants",
                  "Retail commerce engine",
                  "Priority support",
                  "Advanced analytics",
                ].map((x) => <li key={x} className="flex gap-2"><span className="text-emerald-600">✓</span>{x}</li>)}
              </ul>
              <Button
                onClick={() => isCurrent || hasActiveSub ? openPortal() : upgrade(code)}
                disabled={actionBusy === code || actionBusy === "portal" || !s.configured}
                className="w-full mt-6 bg-primary hover:bg-primary/90"
                data-testid={`billing-upgrade-${code}-btn`}
              >
                {actionBusy === code || actionBusy === "portal" ? "Redirecting…" :
                  isCurrent ? "Manage subscription" :
                  hasActiveSub ? "Switch via Billing Portal" :
                  `Upgrade to ${p.name.split(" ").slice(-1)[0]}`}
              </Button>
            </Card>
          );
        })}
      </div>

      {/* ---- Payment History (Iter64.5) ---- */}
      <h2 className="font-display text-2xl font-semibold mt-10 mb-3">Payment history</h2>
      <Card className="p-0 overflow-hidden" data-testid="billing-payment-history">
        {payments === null ? (
          <div className="p-6 text-sm text-muted-foreground" data-testid="billing-payments-loading">Loading payment history…</div>
        ) : payments.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground" data-testid="billing-payments-empty">
            No payments yet. After your first upgrade, invoices and receipts will appear here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5">Date</th>
                  <th className="text-left px-4 py-2.5">Plan</th>
                  <th className="text-right px-4 py-2.5">Amount</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-right px-4 py-2.5">Invoice</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {payments.map((p, i) => {
                  const dt = p.paid_at ? new Date(p.paid_at) : null;
                  const statusColor = {
                    paid: "bg-emerald-100 text-emerald-800",
                    open: "bg-sky-100 text-sky-800",
                    trial_started: "bg-sky-100 text-sky-800",
                    failed: "bg-rose-100 text-rose-800",
                    uncollectible: "bg-rose-100 text-rose-800",
                    void: "bg-slate-100 text-slate-700",
                  }[p.status] || "bg-slate-100 text-slate-700";
                  return (
                    <tr key={p.payment_id || i} data-testid={`billing-payment-row-${i}`}>
                      <td className="px-4 py-2.5 whitespace-nowrap" data-testid={`billing-payment-date-${i}`}>
                        {dt ? dt.toLocaleDateString() + " " + dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                      </td>
                      <td className="px-4 py-2.5 capitalize" data-testid={`billing-payment-plan-${i}`}>
                        {p.plan_code || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums" data-testid={`billing-payment-amount-${i}`}>
                        {p.currency ? p.currency.toUpperCase() : "USD"} {Number(p.amount || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColor}`} data-testid={`billing-payment-status-${i}`}>
                          {(p.status || "").replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-xs">
                        {p.invoice_url ? (
                          <a href={p.invoice_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid={`billing-payment-invoice-link-${i}`}>
                            View invoice ↗
                          </a>
                        ) : p.receipt_url ? (
                          <a href={p.receipt_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid={`billing-payment-receipt-link-${i}`}>
                            View receipt ↗
                          </a>
                        ) : p.invoice_pdf ? (
                          <a href={p.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid={`billing-payment-pdf-link-${i}`}>
                            PDF ↗
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ---- Stripe identifiers (small print, useful for support) ---- */}
      {s.stripe_customer_id && (
        <p className="mt-6 text-[11px] text-muted-foreground font-mono" data-testid="billing-customer-id">
          Customer: {s.stripe_customer_id}
          {s.stripe_subscription_id && <> · Subscription: {s.stripe_subscription_id}</>}
        </p>
      )}
    </DashboardLayout>
  );
}

export function ShareLink() {
  const [bot, setBot] = useState(null);
  const [showcase, setShowcase] = useState(null);
  useEffect(() => {
    api.get("/company").then(r => setBot(r.data.chatbot));
    api.get("/dashboard/showcase").then(r => setShowcase(r.data)).catch(() => {});
  }, []);
  const link = bot ? `${window.location.origin}/chat/${bot.slug}` : "";
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(link)}`;
  const isDemo = showcase?.is_demo_tenant;
  const showcaseUrl = bot ? `${window.location.origin}/showcase/${bot.slug}` : "";
  const embedSnippet = bot
    ? `<iframe src="${window.location.origin}/showcase/${bot.slug}?embed=1" width="100%" height="800" style="border:0;border-radius:12px" allow="autoplay" title="Rudras-AI Live Demo"></iframe>`
    : "";
  return (
    <DashboardLayout>
      <h1 className="font-display text-4xl font-semibold mb-2">Share Your Chatbot</h1>
      <p className="text-muted-foreground mb-8">Print, embed, or DM the link below.</p>
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6">
          <Label>Public URL</Label>
          <div className="font-mono text-sm bg-secondary px-3 py-2 rounded-lg mt-2 break-all" data-testid="share-link">{link}</div>
          <Button className="mt-4 bg-primary hover:bg-primary/90" data-testid="share-copy" onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }}><Copy className="w-4 h-4 mr-2"/>Copy link</Button>
        </Card>
        <Card className="p-6 grid place-items-center">
          {bot && <img src={qr} alt="QR" className="rounded-lg" data-testid="share-qr"/>}
        </Card>
      </div>

      {isDemo && (
        <Card className="p-6 mt-6 bg-gradient-to-br from-emerald-50 to-emerald-100/40 border-emerald-200" data-testid="share-showcase-card">
          <div className="inline-flex items-center gap-2 px-2.5 py-0.5 bg-emerald-200/60 rounded-full text-emerald-900 text-[10px] font-semibold uppercase tracking-wider mb-2">
            <Sparkles className="w-3 h-3"/> Gold Demo · public link
          </div>
          <h2 className="font-display text-2xl font-semibold text-emerald-950">Cinematic Showcase URL</h2>
          <p className="text-sm text-emerald-900/70 mt-1">Marketing-grade public page that auto-plays the walkthrough, lets visitors explore the dashboard interactively, and ends with a "book a custom demo" CTA. Perfect for LinkedIn, conference QR codes, recruiter demos, and investor decks.</p>

          <Label className="mt-4 block">Public showcase URL</Label>
          <div className="font-mono text-xs bg-white px-3 py-2 rounded-lg mt-1 break-all border" data-testid="showcase-public-link">{showcaseUrl}</div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button size="sm" onClick={() => { navigator.clipboard.writeText(showcaseUrl); toast.success("Showcase URL copied"); }} data-testid="showcase-copy-link"><Copy className="w-3.5 h-3.5 mr-1.5"/>Copy URL</Button>
            <a href={showcaseUrl} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" data-testid="showcase-open-link"><ExternalLink className="w-3.5 h-3.5 mr-1.5"/>Open</Button>
            </a>
            <a href={`${showcaseUrl}?mode=interactive`} target="_blank" rel="noreferrer">
              <Button size="sm" variant="ghost" data-testid="showcase-open-interactive">Open interactive only</Button>
            </a>
          </div>

          <Label className="mt-5 block">Embed snippet (iframe)</Label>
          <pre className="font-mono text-[11px] bg-white px-3 py-2 rounded-lg mt-1 border overflow-x-auto" data-testid="showcase-embed-snippet">{embedSnippet}</pre>
          <Button size="sm" variant="outline" className="mt-2" onClick={() => { navigator.clipboard.writeText(embedSnippet); toast.success("Embed snippet copied"); }} data-testid="showcase-copy-embed">
            <Copy className="w-3.5 h-3.5 mr-1.5"/>Copy embed code
          </Button>
        </Card>
      )}
    </DashboardLayout>
  );
}

export function Privacy() {
  const [bot, setBot] = useState({});
  useEffect(() => { api.get("/company").then(r => setBot(r.data.chatbot || {})); }, []);
  const save = async () => { await api.put("/chatbot", { auto_delete_days: bot.auto_delete_days, disclaimer: bot.disclaimer }); toast.success("Saved"); };
  return (
    <DashboardLayout>
      <h1 className="font-display text-4xl font-semibold mb-2">Privacy & Data Retention</h1>
      <p className="text-muted-foreground mb-8">Control how long visitor data is stored.</p>
      <Card className="p-6 max-w-xl space-y-4">
        <div><Label>Auto-delete conversations after (days)</Label><Input type="number" className="mt-1.5" value={bot.auto_delete_days || 90} onChange={e => setBot({...bot, auto_delete_days: parseInt(e.target.value)})}/></div>
        <div><Label>Disclaimer shown to visitors</Label><Textarea rows={3} className="mt-1.5" value={bot.disclaimer || ""} onChange={e => setBot({...bot, disclaimer: e.target.value})}/></div>
        <Button onClick={save} className="bg-primary hover:bg-primary/90">Save</Button>
      </Card>
      <Card className="p-6 mt-6 max-w-xl">
        <h3 className="font-medium mb-2">⚠️ Prohibited uploads</h3>
        <p className="text-sm text-muted-foreground">Do not upload patient data, employee confidential data, financial records, legal case files, or other sensitive regulated information.</p>
      </Card>
    </DashboardLayout>
  );
}


// ============ Email Logs ============
const STATUS_META = {
  sent:           { label: "Sent",           icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  failed:         { label: "Failed",         icon: XCircle,      cls: "text-red-700 bg-red-50 border-red-200" },
  not_configured: { label: "Not configured", icon: AlertCircle,  cls: "text-amber-700 bg-amber-50 border-amber-200" },
};
// Demo-mode delivery_state labels (Healthcare gold-demo tenant only). These
// are presentation-only badges driven by seeded rows where `demo_mode=true`;
// they never indicate a real SendGrid send. See _seed_demo_email_logs in
// /app/backend/demo_seed.py for the source data.
const DEMO_DELIVERY_META = {
  "Simulated Sent":      { icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  "Queued":              { icon: Loader2,      cls: "text-sky-700 bg-sky-50 border-sky-200" },
  "Pending Integration": { icon: AlertCircle,  cls: "text-amber-700 bg-amber-50 border-amber-200" },
  "Demo Mode":           { icon: Sparkles,     cls: "text-violet-700 bg-violet-50 border-violet-200" },
};
const SOURCE_META = {
  transcript:         { label: "Transcript",        cls: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  lead_notification:  { label: "Lead",              cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  demo_request:       { label: "Demo request",      cls: "text-violet-700 bg-violet-50 border-violet-200" },
  summary:            { label: "Summary",           cls: "text-slate-700 bg-slate-100 border-slate-200" },
  pricing_forward:    { label: "Pricing forward",   cls: "text-amber-700 bg-amber-50 border-amber-200" },
  follow_up:          { label: "Follow-up",         cls: "text-sky-700 bg-sky-50 border-sky-200" },
  missed_chat:        { label: "Missed chat",       cls: "text-rose-700 bg-rose-50 border-rose-200" },
};
function StatusBadge({ status, demoMode, deliveryState }) {
  // Demo rows render the friendly `delivery_state` label instead of the raw
  // `not_configured` status, so the audit trail reads as presentation-ready.
  if (demoMode && deliveryState) {
    const dm = DEMO_DELIVERY_META[deliveryState] || { icon: AlertCircle, cls: "text-muted-foreground bg-muted border" };
    const DIcon = dm.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${dm.cls}`} data-testid={`email-log-status-demo-${deliveryState.toLowerCase().replace(/\s+/g, '-')}`}>
        <DIcon className={`w-3 h-3 ${deliveryState === "Queued" ? "animate-spin" : ""}`}/>{deliveryState}
      </span>
    );
  }
  const m = STATUS_META[status] || { label: status || "unknown", icon: AlertCircle, cls: "text-muted-foreground bg-muted border" };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${m.cls}`} data-testid={`email-log-status-${status}`}>
      <Icon className="w-3 h-3"/>{m.label}
    </span>
  );
}
function SourceBadge({ type, label }) {
  const meta = SOURCE_META[type];
  const text = label || meta?.label || type || "—";
  const cls = meta?.cls || "text-muted-foreground bg-muted border";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10.5px] font-medium uppercase tracking-wide ${cls}`} data-testid={`email-log-source-${type || 'unknown'}`}>
      {text}
    </span>
  );
}

export function EmailLogs() {
  const [data, setData] = useState({ items: [], summary: {}, email_configured: false, from_email: null, from_name: null });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [retryingId, setRetryingId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchLogs = useCallback(async (status) => {
    setLoading(true);
    try {
      const params = status && status !== "all" ? { status } : {};
      const { data: payload } = await api.get("/email-logs", { params });
      setData(payload);
      setLastFetched(new Date());
    } catch (err) {
      console.error("[email-logs] fetch failed", err);
      toast.error(err.response?.data?.detail || "Failed to load email logs.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLogs("all"); }, [fetchLogs]);

  const setTab = (val) => { setFilter(val); fetchLogs(val); };

  const retry = async (log) => {
    setRetryingId(log.id);
    try {
      const { data: result } = await api.post(`/email-logs/${log.id}/retry`);
      if (result.status === "sent") toast.success("Resent successfully");
      else if (result.status === "not_configured") toast.message("Email delivery still not configured. Set SENDGRID_API_KEY to enable sending.");
      else toast.error(result.error || "Retry failed — see new log entry for details.");
      fetchLogs(filter);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retry failed.");
    } finally { setRetryingId(null); }
  };

  const openDetail = async (log) => {
    try {
      const { data: full } = await api.get(`/email-logs/${log.id}`);
      setSelected(full);
    } catch (err) {
      toast.error("Failed to load email body.");
    }
  };

  const { items, summary, email_configured, from_email, from_name } = data;
  const counts = {
    all: (summary.sent || 0) + (summary.failed || 0) + (summary.not_configured || 0),
    sent: summary.sent || 0,
    failed: summary.failed || 0,
    not_configured: summary.not_configured || 0,
  };
  // Detect demo-mode rows so we can show the honest "simulated activity"
  // banner. Demo rows are seeded only for the Healthcare gold-demo tenant
  // (see _seed_demo_email_logs in /app/backend/demo_seed.py).
  const hasDemoRows = items.some(l => l.demo_mode);

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <h1 className="font-display text-4xl font-semibold">Email Logs</h1>
        <Button size="sm" variant="outline" onClick={() => fetchLogs(filter)} disabled={loading} data-testid="email-logs-refresh" aria-label="Refresh email logs">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
          <span className="ml-2 hidden sm:inline">Refresh</span>
        </Button>
      </div>
      <p className="text-muted-foreground mb-6">
        Audit trail of every transcript and lead notification sent by your chatbot.
        {lastFetched && <span className="text-xs ml-2">· Updated {lastFetched.toLocaleTimeString()}</span>}
      </p>

      {/* Config banner */}
      <Card className={`p-4 mb-6 border ${email_configured ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="email-config-banner">
        <div className="flex items-start gap-3">
          <Mail className={`w-5 h-5 mt-0.5 ${email_configured ? "text-emerald-700" : "text-amber-700"}`}/>
          <div className="text-sm">
            {email_configured ? (
              <>
                <div className="font-medium text-emerald-900">Email delivery is active</div>
                <div className="text-emerald-800/80 mt-0.5">
                  Sending as <span className="font-mono">{from_name ? `${from_name} <${from_email}>` : from_email}</span> via SendGrid.
                </div>
              </>
            ) : (
              <>
                <div className="font-medium text-amber-900">Email delivery is not configured yet</div>
                <div className="text-amber-800/80 mt-0.5">
                  Transcript &amp; lead requests are being saved here for audit. Add <code className="font-mono text-xs">SENDGRID_API_KEY</code> in your environment and restart the backend to begin delivery.
                </div>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Demo-mode banner — only when the tenant has seeded simulated rows.
          Kept distinct from the not-configured banner so technical evaluators
          immediately see this audit trail is presentation data, not real
          SendGrid traffic. */}
      {hasDemoRows && (
        <Card className="p-4 mb-6 border bg-violet-50 border-violet-200" data-testid="email-demo-banner">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 mt-0.5 text-violet-700"/>
            <div className="text-sm">
              <div className="font-medium text-violet-900">Demo environment — simulated email activity</div>
              <div className="text-violet-800/80 mt-0.5">
                The rows below are seeded for product demos, investor walkthroughs, and
                recruiter presentations. No emails were actually delivered. Live delivery
                requires SendGrid configuration (<code className="font-mono text-xs">SENDGRID_API_KEY</code>
                {' '}+ <code className="font-mono text-xs">SENDGRID_FROM_EMAIL</code>).
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Status tabs */}
      <Tabs value={filter} onValueChange={setTab} className="mb-4">
        <TabsList data-testid="email-logs-tabs">
          <TabsTrigger value="all" data-testid="tab-all">All <span className="ml-1.5 text-xs text-muted-foreground">{counts.all}</span></TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent">Sent <span className="ml-1.5 text-xs text-muted-foreground">{counts.sent}</span></TabsTrigger>
          <TabsTrigger value="failed" data-testid="tab-failed">Failed <span className="ml-1.5 text-xs text-muted-foreground">{counts.failed}</span></TabsTrigger>
          <TabsTrigger value="not_configured" data-testid="tab-not-configured">Not configured <span className="ml-1.5 text-xs text-muted-foreground">{counts.not_configured}</span></TabsTrigger>
        </TabsList>
        <TabsContent value={filter}>
          <Card className="p-0 overflow-hidden" data-testid="email-logs-card">
            {loading && items.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin"/> Loading email logs…
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-16 px-6 text-muted-foreground" data-testid="email-logs-empty">
                <Mail className="w-10 h-10 mx-auto mb-3 opacity-40"/>
                <div className="font-medium text-foreground mb-1">No email activity yet</div>
                <div className="text-sm max-w-sm mx-auto">When a visitor requests a transcript or submits a lead, the delivery attempt will appear here.</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Provider response</TableHead>
                      <TableHead>When</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map(log => {
                      const isDemo = Boolean(log.demo_mode);
                      // Live (non-demo) rows can be retried; demo rows are
                      // presentation-only so we hide the retry CTA.
                      const canRetry = !isDemo && (log.status === "failed" || log.status === "not_configured");
                      let providerInfo;
                      if (isDemo) {
                        providerInfo = <span className="text-xs text-muted-foreground italic">Simulated — no provider call</span>;
                      } else if (log.status === "sent") {
                        providerInfo = log.provider_message_id
                          ? <span className="font-mono text-xs">{log.provider_message_id}</span>
                          : <span className="text-xs text-muted-foreground">{log.provider_status || "202"}</span>;
                      } else if (log.error) {
                        providerInfo = <span className="text-xs text-red-700 truncate inline-block max-w-[220px]" title={log.error}>{log.error}</span>;
                      } else {
                        providerInfo = <span className="text-xs text-muted-foreground">—</span>;
                      }
                      const recipientLabel = log.to_name ? (
                        <div className="flex flex-col">
                          <span className="font-medium leading-tight">{log.to_name}</span>
                          <span className="text-xs text-muted-foreground font-mono leading-tight">{log.to}</span>
                        </div>
                      ) : (
                        <span className="font-medium">{log.to}</span>
                      );
                      return (
                        <TableRow key={log.id} data-testid={`email-log-row-${log.id}`}>
                          <TableCell><StatusBadge status={log.status} demoMode={isDemo} deliveryState={log.delivery_state}/></TableCell>
                          <TableCell><SourceBadge type={log.source_type} label={log.source_label}/></TableCell>
                          <TableCell>{recipientLabel}</TableCell>
                          <TableCell className="max-w-[280px] truncate" title={log.subject}>{log.subject}</TableCell>
                          <TableCell>{providerInfo}</TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{new Date(log.sent_at).toLocaleString()}</TableCell>
                          <TableCell className="text-right space-x-1 whitespace-nowrap">
                            <Button size="sm" variant="ghost" onClick={() => openDetail(log)} data-testid={`email-log-view-${log.id}`}>View</Button>
                            {canRetry && (
                              <Button size="sm" variant="outline" onClick={() => retry(log)} disabled={retryingId === log.id} data-testid={`email-log-retry-${log.id}`}>
                                {retryingId === log.id ? <Loader2 className="w-3 h-3 animate-spin"/> : <Send className="w-3 h-3"/>}
                                <span className="ml-1.5">Retry</span>
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail drawer (inline panel for simplicity) */}
      {selected && (
        <Card className="p-5 mt-6" data-testid="email-log-detail">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={selected.status} demoMode={Boolean(selected.demo_mode)} deliveryState={selected.delivery_state}/>
                {selected.source_type && <SourceBadge type={selected.source_type} label={selected.source_label}/>}
                <span className="text-sm text-muted-foreground">{new Date(selected.sent_at).toLocaleString()}</span>
              </div>
              <h3 className="font-display text-xl mt-1">{selected.subject}</h3>
              <div className="text-sm text-muted-foreground mt-0.5">
                To: {selected.to_name && <span className="font-medium text-foreground">{selected.to_name}</span>} <span className="font-mono">{selected.to_name ? `<${selected.to}>` : selected.to}</span>
              </div>
              {selected.provider_message_id && <div className="text-xs text-muted-foreground mt-0.5">Message ID: <span className="font-mono">{selected.provider_message_id}</span></div>}
              {selected.error && <div className="text-xs text-red-700 mt-1">Error: {selected.error}</div>}
              {selected.demo_mode && <div className="text-xs text-violet-700 mt-1 italic">Demo audit row — no SendGrid call was made.</div>}
            </div>
            <Button size="sm" variant="ghost" onClick={() => setSelected(null)} data-testid="email-log-detail-close">Close</Button>
          </div>
          <pre className="text-sm bg-muted/40 rounded p-3 whitespace-pre-wrap break-words max-h-96 overflow-y-auto" data-testid="email-log-body">{selected.body || "(no body)"}</pre>
        </Card>
      )}
    </DashboardLayout>
  );
}

// ============ Reusable Change-Password form (shared by customer + internal admin) ============
export function ChangePasswordCard({ testidPrefix = "pwd" }) {
  const { updateToken, user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  // v1.0.16 — dirty whenever ANY of the three fields has content; the
  // password card never "loads" a server value so a non-empty field is
  // always unsaved.
  const isDirty = Boolean(form.current_password || form.new_password || form.confirm);
  useUnsavedChangesGuard(isDirty);
  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast.error("New passwords don't match"); return; }
    if (form.new_password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: form.current_password, new_password: form.new_password,
      });
      if (data.token) updateToken(data.token, user);
      toast.success("Password updated. You're still signed in with a fresh token.");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.[0]?.msg || "Password change failed"));
    } finally { setLoading(false); }
  };
  return (
    <Card className="p-6 max-w-xl" data-testid={`${testidPrefix}-change-card`}>
      <h2 className="font-display text-xl font-semibold mb-1">Change password</h2>
      <p className="text-sm text-muted-foreground mb-4">Update your sign-in password. Minimum 8 characters.</p>
      <form onSubmit={submit} className="space-y-3" data-testid={`${testidPrefix}-form`}>
        <div>
          <Label>Current password</Label>
          <Input type="password" required value={form.current_password} onChange={(e) => setForm({...form, current_password: e.target.value})} className="mt-1.5" data-testid={`${testidPrefix}-current`}/>
        </div>
        <div>
          <Label>New password (min 8)</Label>
          <Input type="password" required minLength={8} value={form.new_password} onChange={(e) => setForm({...form, new_password: e.target.value})} className="mt-1.5" data-testid={`${testidPrefix}-new`}/>
        </div>
        <div>
          <Label>Confirm new password</Label>
          <Input type="password" required minLength={8} value={form.confirm} onChange={(e) => setForm({...form, confirm: e.target.value})} className="mt-1.5" data-testid={`${testidPrefix}-confirm`}/>
        </div>
        <Button type="submit" disabled={loading} className="bg-primary hover:bg-primary/90" data-testid={`${testidPrefix}-submit`}>{loading ? "Updating..." : "Update password"}</Button>
      </form>
    </Card>
  );
}

// ============ Customer "Security" page (Change password) ============
export function CustomerSecurity() {
  const { user } = useAuth();
  return (
    <DashboardLayout>
      <h1 className="font-display text-4xl font-semibold mb-2">Account &amp; security</h1>
      <p className="text-muted-foreground mb-8">Signed in as <span className="font-medium text-foreground">{user?.email}</span></p>
      <ChangePasswordCard testidPrefix="customer-pwd"/>
    </DashboardLayout>
  );
}

// ============ Customer "Team Management" shell ============
export function CustomerTeam() {
  const { user } = useAuth();
  return (
    <DashboardLayout>
      <h1 className="font-display text-4xl font-semibold mb-2">Team</h1>
      <p className="text-muted-foreground mb-8">Manage who can access this assistant. Multi-user invites ship in the next milestone.</p>
      <Card className="p-0 overflow-hidden max-w-3xl" data-testid="team-card">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            <TableRow data-testid="team-row-self">
              <TableCell className="font-medium">{user?.full_name}</TableCell>
              <TableCell>{user?.email}</TableCell>
              <TableCell><Badge>{user?.role}</Badge></TableCell>
              <TableCell><Badge variant="default">Active (you)</Badge></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>
      <Card className="p-5 mt-6 max-w-3xl bg-muted/30" data-testid="team-invite-shell">
        <div className="flex items-start gap-3">
          <Users className="w-5 h-5 mt-0.5 text-muted-foreground"/>
          <div className="text-sm">
            <div className="font-medium text-foreground mb-1">Invite teammates — coming soon</div>
            <div className="text-muted-foreground">In the next milestone you'll be able to invite editors, viewers, and analytics-only collaborators. For now, every assistant has a single business admin.</div>
          </div>
        </div>
      </Card>
    </DashboardLayout>
  );
}


// ============ Embed Widget Page ============
// Architecture: iframe-based. We host the chat at /chat/{slug}?embed=1 which
// renders a chrome-free version of PublicChatbot. Customers paste either:
//   1. <iframe> tag — full-page embed (e.g. their /contact page)
//   2. A small JS snippet that injects a floating "Chat" bubble bottom-right
// Both snippets only reference the public slug — zero exposure of internal APIs.
export function EmbedWidget() {
  const [info, setInfo] = useState(null);
  const [tab, setTab] = useState("bubble");
  useEffect(() => { api.get("/company").then(r => setInfo(r.data)); }, []);
  const slug = info?.chatbot?.slug;
  const themeColor = info?.chatbot?.theme_color || "#E8A33D";
  const origin = window.location.origin;
  const chatUrl = slug ? `${origin}/chat/${slug}?embed=1` : "";

  const iframeSnippet = slug ? `<!-- Rudras-AI chatbot — paste anywhere on your site -->
<iframe
  src="${chatUrl}"
  title="Chat with us"
  style="border:0;width:100%;max-width:420px;height:600px;border-radius:16px;box-shadow:0 12px 40px rgba(0,0,0,.12);"
  allow="microphone"
></iframe>` : "";

  const bubbleSnippet = slug ? `<!-- Rudras-AI floating chat bubble — paste before </body> -->
<script>
(function(){
  var s="${slug}", o="${origin}", c="${themeColor}";
  var b=document.createElement('button');
  b.setAttribute('aria-label','Chat with us');
  b.style.cssText='position:fixed;bottom:24px;right:24px;width:56px;height:56px;border-radius:50%;background:'+c+';color:#fff;border:0;box-shadow:0 6px 18px rgba(0,0,0,.25);font-size:24px;cursor:pointer;z-index:2147483646;';
  b.innerHTML='&#128172;';
  var f=document.createElement('iframe');
  f.title='Chat with us';
  f.allow='microphone';
  f.style.cssText='position:fixed;bottom:96px;right:24px;width:380px;max-width:calc(100vw - 32px);height:600px;max-height:calc(100vh - 120px);border:0;border-radius:16px;box-shadow:0 12px 40px rgba(0,0,0,.18);z-index:2147483645;display:none;background:#fff;';
  f.src=o+'/chat/'+s+'?embed=1';
  document.body.appendChild(f);
  document.body.appendChild(b);
  b.onclick=function(){f.style.display=f.style.display==='none'?'block':'none';};
})();
</script>` : "";

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Snippet copied");
  };

  return (
    <DashboardLayout>
      <h1 className="font-display text-4xl font-semibold mb-2">Embed</h1>
      <p className="text-muted-foreground mb-6">Drop your chatbot into any website. Both snippets use an iframe — fully style-isolated, mobile-responsive, no CSS conflicts with your site.</p>

      {!slug ? (
        <Card className="p-6 text-muted-foreground">Loading your chatbot…</Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="p-6 lg:col-span-2" data-testid="embed-card">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList data-testid="embed-tabs">
                <TabsTrigger value="bubble" data-testid="embed-tab-bubble">Floating bubble (JS)</TabsTrigger>
                <TabsTrigger value="iframe" data-testid="embed-tab-iframe">Inline iframe</TabsTrigger>
              </TabsList>
              <TabsContent value="bubble" className="space-y-3">
                <p className="text-sm text-muted-foreground">Shows a small chat bubble in the bottom-right of every page on your site. Visitors click it to open the chatbot.</p>
                <Textarea readOnly className="font-mono text-xs h-44" value={bubbleSnippet} data-testid="embed-bubble-snippet"/>
                <Button onClick={() => copy(bubbleSnippet)} data-testid="embed-bubble-copy"><Copy className="w-4 h-4 mr-2"/>Copy snippet</Button>
              </TabsContent>
              <TabsContent value="iframe" className="space-y-3">
                <p className="text-sm text-muted-foreground">Embeds the chatbot inline as a fixed-size panel — useful on a Contact page or sidebar.</p>
                <Textarea readOnly className="font-mono text-xs h-32" value={iframeSnippet} data-testid="embed-iframe-snippet"/>
                <Button onClick={() => copy(iframeSnippet)} data-testid="embed-iframe-copy"><Copy className="w-4 h-4 mr-2"/>Copy snippet</Button>
              </TabsContent>
            </Tabs>
            <div className="mt-6 border-t pt-4 text-xs text-muted-foreground space-y-1">
              <div><strong className="text-foreground">Direct chat URL:</strong> <a href={chatUrl} target="_blank" rel="noreferrer" className="font-mono text-accent hover:underline break-all" data-testid="embed-direct-url">{chatUrl}</a></div>
              <div>Voice chat requires the host site to be served over HTTPS and grant microphone permission.</div>
              <div>Branding (logo, colour, welcome message) updates instantly — no need to re-paste the snippet.</div>
            </div>
          </Card>
          <Card className="p-5 h-fit lg:sticky lg:top-6" data-testid="embed-preview">
            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">Live preview</div>
            <iframe src={chatUrl} title="Chat preview" className="w-full h-[520px] rounded-lg border" allow="microphone"/>
          </Card>
        </div>
      )}
    </DashboardLayout>
  );
}
